import './CashierPage.desktop.css';
import './CashierPage.mobile.css';
import moment from 'moment-timezone';
import { api } from '../services/api.js';
import { Notification } from '../components/Notification.js';
import { Footer } from '../components/Footer.js';
import { authService as actualAuthService } from '../services/authService.js';
import { cashierContext } from '../context/CashierContext.js';
import { CashierHeader } from '../components/CashierHeader.js';
import { printTicketReceipt } from '../utils/printService.js';
import { loadAndRenderFinishedRace } from '../components/cashier/FinishedRaceBoard.js';
import { openPayoutModal } from '../components/cashier/PayoutModal.js';
import { showCashReconciliation, showTicketList } from '../components/cashier/ReportModals.js';
import { generateDailySchedulePDF, generateGazettePDF } from '../utils/pdfGenerator.js';

const VENEZUELA_TIMEZONE = 'America/Caracas';

// Inyección de CSS de seguridad para contraste de matriz y bloqueo de botones
const extraStyles = document.createElement('style');
extraStyles.textContent = `
    .race-btn:disabled, .race-btn.closed-race { 
        background-color: #450a0a !important; 
        border-color: #ef4444 !important; 
        color: #fca5a5 !important; 
        opacity: 0.6; 
        cursor: not-allowed !important; 
    }
    .race-btn:disabled span, .race-btn.closed-race span { 
        color: #fca5a5 !important; 
    }
    .matrix-input {
        color: #ffffff !important;
        font-weight: 900 !important;
        text-shadow: 0px 0px 4px rgba(255, 255, 255, 0.4);
    }
    .matrix-input:focus {
        outline: none;
        border: 4px solid #D4AF37 !important;
        background-color: #052e16 !important;
        color: #4ade80 !important; 
        text-shadow: 0px 0px 8px rgba(74, 222, 128, 0.6);
    }
`;
document.head.appendChild(extraStyles);

const wizardStyles = document.createElement('style');
wizardStyles.textContent = `
/* --- 🚀 ESTILOS COMPARTIDOS DEL WIZARD (NO TOCAR) --- */
.center-betting-area { flex-grow: 1; display: flex; flex-direction: column; }

/* --- 🚀 NUEVOS ESTILOS PARA MATRIZ TABULAR DE EXÓTICAS --- */
.exotic-wizard-pos { display: flex; flex-direction: column; width: 100%; height: 100%; gap: 0.5rem; overflow: hidden; }
.exo-matrix-cell {
    width: 32px;
    height: 32px;
    margin: 0 auto;
    background-color: #0f172a;
    border: 2px solid #334155;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: transparent;
    font-weight: 900;
    cursor: pointer;
    transition: all 0.1s;
}
.exo-matrix-cell:hover { background-color: #1e293b; }
.exo-matrix-cell.selected { 
    background-color: #D4AF37; 
    border-color: #000; 
    color: #000; 
    box-shadow: inset 0 0 0 2px black; 
}
.bet-matrix-container::-webkit-scrollbar { width: 6px; height: 6px; }
.bet-matrix-container::-webkit-scrollbar-thumb { background: #047857; border-radius: 3px; }
.mobile-only-btn { display: none !important; }
@media (max-width: 1024px) {
    .mobile-only-btn { display: flex !important; }
    .desktop-only-btn { display: none !important; }
}
`;
document.head.appendChild(wizardStyles);

export const authService = {
    ...actualAuthService,
    getCurrentUser: () => {
        const user = actualAuthService.getCurrentUser();
        if (user) return user;
        const fallbackId = "temp_" + Math.random().toString(36).substr(2, 9);
        return { id: fallbackId, username: "Cajero Temporal" };
    }
};

const checkAuthError = (e) => {
    const msg = String(e?.message || e?.error || e);
    if (e?.status === 401 || e?.response?.status === 401 || msg.includes('401') || /token|expirad|autorizad|unauthorized/i.test(msg)) {
        authService.logout(); window.location.href = '/login'; return true;
    }
    return false;
};

let appState = {
    exchangeRate: 0,
    keyboardMode: 'MATRIX',
    bettingMode: 'DIRECT', // 'DIRECT' o 'EXOTIC'
    globalKeyHandler: null, 
    tracksByLetter: {}, 
    domElements: {
        notificationContainerCashier: null
    }
};

const getHorseColor = (number) => {
    const colors = {
        1: { bg: '#e91c24', text: '#ffffff' }, 2: { bg: '#ffffff', text: '#000000' },
        3: { bg: '#005eb8', text: '#ffffff' }, 4: { bg: '#ffde00', text: '#000000' },
        5: { bg: '#009639', text: '#ffffff' }, 6: { bg: '#000000', text: '#ffde00' },
        7: { bg: '#ff7900', text: '#000000' }, 8: { bg: '#f472d0', text: '#000000' },
        9: { bg: '#00bce4', text: '#000000' }, 10: { bg: '#672146', text: '#ffffff' },
        11: { bg: '#9ca3af', text: '#b91c1c' }, 12: { bg: '#84cc16', text: '#000000' },
        13: { bg: '#78350f', text: '#ffffff' }, 14: { bg: '#7f1d1d', text: '#ffffff' },
        15: { bg: '#fef08a', text: '#000000' }
    };
    return colors[number] || { bg: '#64748b', text: '#ffffff' }; 
};

function formatDualCurrency(amount, currencyCode) {
    const rate = appState.exchangeRate;
    const val = parseFloat(amount);
    if (!rate || rate <= 0) {
        return `${currencyCode === 'USD' ? '$' : (currencyCode === 'COP' ? 'COL$' : 'Bs.')}${val.toFixed(2)}`;
    }
    if (currencyCode === 'USD') return `$${val.toFixed(2)} <span class="text-xs text-gray-500">(Bs. ${(val * rate).toFixed(2)})</span>`;
    if (currencyCode === 'COP') return `COL$${val.toFixed(2)} <span class="text-xs text-gray-500">($${(val / rate).toFixed(2)})</span>`;
    return `Bs. ${val.toFixed(2)} <span class="text-xs text-gray-500">($${(val / rate).toFixed(2)})</span>`;
}

function calculateTimeRemaining(startTimeUTC) {
    const distance = startTimeUTC.diff(moment.utc());
    if (distance < 0) return 'MTP 0';
    const duration = moment.duration(distance);
    return `${String(Math.floor(duration.asHours())).padStart(2, '0')}:${String(duration.minutes()).padStart(2, '0')}:${String(duration.seconds()).padStart(2, '0')}`;
}

function renderExoticWizard(state, renderDashboard) {
    const container = document.getElementById('exotic-wizard-container');
    if (!container) return;

    // Contención estricta para evitar que los Flex-Items se salgan de pantalla en el POS
    container.style.display = 'flex';
    container.style.flexDirection = 'column';
    container.style.height = '100%';
    container.style.overflow = 'hidden';
    
    const currencyObj = state.availableCurrencies.find(c => c.code === state.currency);
    const currencySym = currencyObj ? currencyObj.symbol : '$';
    const activeHorses = (state.selectedRace.runners || []).filter(r => !r.is_scratched);

    const betTypeDefs = {
        'EXACTA': { pos: 2, name: 'Exacta', defBase: 1.00 },
        'TRIFECTA': { pos: 3, name: 'Trifecta', defBase: 0.50 },
        'SUPERFECTA': { pos: 4, name: 'Superfecta', defBase: 0.10 },
        'DAILY_DOUBLE': { pos: 2, name: 'Daily Double', defBase: 2.00 },
        'PICK_3': { pos: 3, name: 'Pick 3', defBase: 0.50 }
    };

    // ⭐ FIX: Inicializa en modo RUEDA por defecto (3 Posiciones para Trifecta)
    const wizardState = {
        selections: [new Set(), new Set(), new Set()] 
    };

    container.innerHTML = `
        <div style="display:flex; gap:10px; margin-bottom:10px; flex-shrink:0;">
            <select id="exo-type-select" style="flex:1; background:#022c22; color:#4ade80; border:1px solid #D4AF37; padding:8px; border-radius:5px; font-weight:bold; font-size:1rem; outline:none; cursor:pointer;">
                <option value="EXACTA">Exacta (1-2)</option>
                <option value="TRIFECTA" selected>Trifecta (1-2-3)</option>
                <option value="SUPERFECTA">Superfecta (1-2-3-4)</option>
                <option value="DAILY_DOUBLE">Daily Double</option>
                <option value="PICK_3">Pick 3</option>
            </select>
            <select id="exo-mode-select" style="flex:1; background:#022c22; color:#4ade80; border:1px solid #D4AF37; padding:8px; border-radius:5px; font-weight:bold; font-size:1rem; outline:none; cursor:pointer;">
                <option value="CAJA">Candado (Box)</option>
                <option value="RUEDA" selected>En Rueda</option>
            </select>
            <button id="btn-exo-guide" style="background:#D4AF37; color:black; font-weight:bold; border-radius:5px; padding:0 15px; border:none; cursor:pointer; flex-shrink:0; box-shadow: 0 2px 4px rgba(0,0,0,0.5);" title="Guía">❓ AYUDA</button>
        </div>
        
        <div id="exo-instruction-banner" style="background: rgba(16, 185, 129, 0.1); border-left: 4px solid #10b981; padding: 8px; font-size: 0.85rem; color: #a7f3d0; margin-bottom: 10px; line-height: 1.3; flex-shrink:0;">
            📌 <b>EN RUEDA:</b> Selecciona al menos 1 ejemplar para <b>CADA</b> posición obligatoria.
        </div>

        <div class="bet-matrix-container" style="flex: 1 1 auto; overflow-y: auto; border: 1px solid #047857; border-radius: 8px; margin-bottom: 10px; max-height: 250px; background:#000;">
            <table class="bet-matrix" id="exo-grid-table" style="width:100%;">
                <!-- Se llena dinámicamente -->
            </table>
        </div>

        <div style="display:flex; justify-content:space-between; align-items:center; background:#022c22; padding:10px 15px; border-radius:8px; border:1px solid #D4AF37; margin-bottom:10px; flex-shrink:0;">
            <div>
                <label style="color:#94a3b8; font-size:0.8rem; font-weight:bold; display:block; margin-bottom:3px;">Costo Base Fracción</label>
                <div style="display:flex; align-items:center; gap:5px;">
                    <span style="color:#10b981; font-weight:bold; font-size:1.1rem;">${currencySym}</span>
                    <input type="number" id="exo-base-input" value="0.50" step="0.10" style="width:70px; background:#000; color:white; border:1px solid #334155; padding:5px; border-radius:4px; font-weight:bold; text-align:center; font-size:1rem; outline:none;">
                </div>
            </div>
            <div style="text-align:right;">
                <div id="exo-combs-display" style="color:#a7f3d0; font-size:0.9rem; font-weight:bold;">0 comb.</div>
                <div id="exo-total-display" style="color:#D4AF37; font-size:1.6rem; font-weight:900; line-height:1;">${currencySym}0.00</div>
            </div>
        </div>

        <button id="btn-commit-exotic" style="background:#15803d; color:white; border:2px solid #4ade80; padding:12px; font-size:1.2rem; font-weight:bold; border-radius:8px; cursor:not-allowed; flex-shrink:0; transition:all 0.2s; opacity:0.4;" disabled>
            ⚠️ COMPLETE SELECCIÓN
        </button>
        
        <!-- ⭐ FIX: BOTONES DE NAVEGACIÓN ESTILIZADOS -->
        <div style="display: flex; gap: 10px; margin-top: 10px; flex-shrink: 0;">
            <button id="tab-direct" style="flex:1; padding: 12px; font-size: 1.1rem; font-weight: bold; border-radius: 8px; cursor: pointer; border: 2px solid #047857; background: #022c22; color: #94a3b8; transition: all 0.2s; box-shadow: 0 4px 6px rgba(0,0,0,0.3);" onmouseover="this.style.background='#047857'; this.style.color='white'" onmouseout="this.style.background='#022c22'; this.style.color='#94a3b8'">
                <i class="fas fa-arrow-left"></i> VOLVER A DIRECTAS [F1]
            </button>
            <button id="tab-exotic" style="flex:1; padding: 12px; font-size: 1.1rem; font-weight: bold; border-radius: 8px; cursor: pointer; border: 2px solid #D4AF37; background: #D4AF37; color: black; box-shadow: 0 4px 6px rgba(0,0,0,0.3);">
                🎡 MODO EXÓTICAS [F2]
            </button>
        </div>
    `;

    const typeSelect = container.querySelector('#exo-type-select');
    const modeSelect = container.querySelector('#exo-mode-select');
    const baseInput = container.querySelector('#exo-base-input');
    const tableEl = container.querySelector('#exo-grid-table');
    const combsDisplay = container.querySelector('#exo-combs-display');
    const totalDisplay = container.querySelector('#exo-total-display');
    const commitBtn = container.querySelector('#btn-commit-exotic');
    const bannerEl = container.querySelector('#exo-instruction-banner');
    const btnExoGuide = container.querySelector('#btn-exo-guide');

    const updateInstructions = () => {
        const type = typeSelect.value;
        const mode = modeSelect.value;
        const posCount = betTypeDefs[type].pos;
        if (mode === 'CAJA') {
            bannerEl.innerHTML = `📌 <b>CANDADO (BOX):</b> Selecciona <b>${posCount} o más</b> ejemplares. Todas las combinaciones posibles quedarán cubiertas.`;
        } else {
            bannerEl.innerHTML = `📌 <b>EN RUEDA:</b> Selecciona al menos 1 ejemplar para <b>CADA</b> posición obligatoria (del 1ro al ${posCount}do).`;
        }
    };

    btnExoGuide.onclick = () => {
        const modal = document.createElement('div');
        modal.className = 'modal-overlay visible';
        modal.style.zIndex = '100000';
        modal.innerHTML = `
            <div class="modal-content" style="background:#1e293b; border:2px solid #D4AF37; text-align:left; max-width: 500px; padding:25px;">
                <h3 style="color:#D4AF37; font-size:1.3rem; margin-top:0; border-bottom:1px solid #334155; padding-bottom:10px;"><i class="fas fa-book"></i> Guía de Exóticas</h3>
                <h4 style="color:#4ade80; margin-bottom:5px;">🔒 CANDADO (BOX)</h4>
                <p style="color:#cbd5e1; font-size:0.85rem; margin-top:0; margin-bottom:15px; line-height:1.4;">Cubre todas las combinaciones posibles entre los caballos seleccionados sin importar el orden.</p>
                <h4 style="color:#60a5fa; margin-bottom:5px;">🎡 EN RUEDA</h4>
                <p style="color:#cbd5e1; font-size:0.85rem; margin-top:0; margin-bottom:15px; line-height:1.4;">Debes seleccionar caballos específicos para el 1er lugar, 2do lugar, etc.</p>
                <button class="btn-huge-primary" style="width:100%; font-size:1rem; padding:10px;" onclick="this.closest('.modal-overlay').remove()">Entendido</button>
            </div>
        `;
        document.body.appendChild(modal);
    };

    const renderGrid = () => {
        const type = typeSelect.value;
        const mode = modeSelect.value;
        const posCount = betTypeDefs[type].pos;

        if (mode === 'CAJA' && wizardState.selections.length !== 1) {
            wizardState.selections = [new Set()];
        } else if (mode === 'RUEDA' && wizardState.selections.length !== posCount) {
            wizardState.selections = Array.from({length: posCount}, () => new Set());
        }

        let html = '<thead><tr><th style="width:40px; text-align:center;">N°</th><th style="text-align:left;">EJEMPLAR</th>';
        
        if (mode === 'CAJA') {
            html += '<th style="text-align:center;">SELECCIÓN BOX</th>';
        } else {
            for (let i = 1; i <= posCount; i++) {
                html += `<th style="text-align:center;">${i}ro</th>`;
            }
        }
        html += '</tr></thead><tbody>';

        if (activeHorses.length === 0) {
            html += '<tr><td colspan="10" style="text-align:center; padding:20px; color:#facc15;">No hay caballos disponibles.</td></tr>';
        } else {
            activeHorses.forEach((h, hIdx) => {
                const bg = getHorseColor(h.horse_number).bg || '#333';
                const txt = getHorseColor(h.horse_number).text || 'white';
                
                html += `<tr>
                    <td style="font-weight:900; color:${txt}; background-color:${bg}; font-size:1.1rem; border:1px solid #000; text-align:center;">${h.horse_number}</td>
                    <td style="text-align:left; font-weight:bold; color:white; font-size:1rem; white-space:nowrap;">${h.name}</td>`;
                
                if (mode === 'CAJA') {
                    const isSelected = wizardState.selections[0].has(hIdx);
                    html += `
                        <td style="text-align:center; vertical-align:middle; padding:4px;">
                            <div class="exo-matrix-cell ${isSelected ? 'selected' : ''}" data-row="0" data-col="${hIdx}">✓</div>
                        </td>
                    `;
                } else {
                    for (let i = 0; i < posCount; i++) {
                        const isSelected = wizardState.selections[i].has(hIdx);
                        html += `
                            <td style="text-align:center; vertical-align:middle; padding:4px;">
                                <div class="exo-matrix-cell ${isSelected ? 'selected' : ''}" data-row="${i}" data-col="${hIdx}">✓</div>
                            </td>
                        `;
                    }
                }
                html += '</tr>';
            });
        }
        html += '</tbody>';
        tableEl.innerHTML = html;

        tableEl.querySelectorAll('.exo-matrix-cell').forEach(cell => {
            cell.addEventListener('click', () => {
                const r = parseInt(cell.dataset.row);
                const c = parseInt(cell.dataset.col);
                if (wizardState.selections[r].has(c)) wizardState.selections[r].delete(c);
                else wizardState.selections[r].add(c);
                renderGrid();
            });
        });
        calculateTotals();
    };

    const calculateTotals = () => {
        const type = typeSelect.value;
        const mode = modeSelect.value;
        const posCount = betTypeDefs[type].pos;
        const baseCost = parseFloat(baseInput.value) || 0;
        let combs = 0;
        let isValid = false;

        if (mode === 'CAJA') {
            const n = wizardState.selections[0].size;
            if (n >= posCount) {
                isValid = true;
                combs = 1;
                for (let i = 0; i < posCount; i++) combs *= (n - i);
            }
        } else {
            const allFilled = wizardState.selections.every(set => set.size > 0);
            if (allFilled) {
                const getCombs = (idx = 0, current = []) => {
                    if (idx === posCount) { combs++; return; }
                    for (const h of wizardState.selections[idx]) {
                        if (!current.includes(h)) getCombs(idx + 1, [...current, h]);
                    }
                };
                getCombs();
                if (combs > 0) isValid = true;
            }
        }

        const total = combs * baseCost;
        combsDisplay.textContent = `${combs} comb.`;
        totalDisplay.textContent = `${currencySym}${total.toFixed(2)}`;

        if (isValid && combs > 0 && baseCost > 0) {
            commitBtn.disabled = false;
            commitBtn.style.opacity = '1';
            commitBtn.style.cursor = 'pointer';
            commitBtn.innerHTML = '➕ AGREGAR AL TICKET';
        } else {
            commitBtn.disabled = true;
            commitBtn.style.opacity = '0.5';
            commitBtn.style.cursor = 'not-allowed';
            commitBtn.innerHTML = '⚠️ COMPLETE SELECCIÓN';
        }
        return { combs, cost: total, isValid };
    };

    typeSelect.addEventListener('change', () => { baseInput.value = betTypeDefs[typeSelect.value].defBase.toFixed(2); updateInstructions(); renderGrid(); });
    modeSelect.addEventListener('change', () => { updateInstructions(); renderGrid(); });
    baseInput.addEventListener('input', calculateTotals);

    const addExoticBet = () => {
        const { combs, cost, isValid } = calculateTotals();
        if (!isValid || combs === 0 || cost <= 0) return;

        const mode = modeSelect.value;
        const type = typeSelect.value;
        const posCount = betTypeDefs[type].pos;
        
        const selectionsObj = {};
        if (mode === 'CAJA') {
            for (let r = 0; r < posCount; r++) { wizardState.selections[0].forEach(hIdx => { selectionsObj[`${r}_${hIdx}`] = true; }); }
        } else {
            for (let r = 0; r < posCount; r++) { wizardState.selections[r].forEach(hIdx => { selectionsObj[`${r}_${hIdx}`] = true; }); }
        }

        const betDetails = {
            type: type,
            selections: selectionsObj,
            runners: activeHorses.map(r => ({ id: r.id, horse_number: r.horse_number, name: r.name })),
            isBox: mode === 'CAJA',
            combinations: combs,
            multiplier: 1, 
            quantity: 1,
            cost: cost
        };

        const newBet = { 
            type: 'EXOTIC', 
            details: betDetails, 
            horseId: 0, 
            horseNumber: 'EXO', 
            horseName: `${type} ${mode === 'CAJA' ? 'BOX' : 'RUEDA'}`, 
            win: cost, place: 0, show: 0 
        };
        
        cashierContext.update({ bets: [...cashierContext.state.bets, newBet] });
        
        // Reseteo inteligente para la próxima jugada (Mantiene Rueda si estaba en Rueda)
        wizardState.selections = mode === 'CAJA' ? [new Set()] : Array.from({length: posCount}, () => new Set());
        renderGrid();
        
        setTimeout(() => { 
            const t = document.getElementById('ticket-scroll-area'); 
            if(t) t.scrollTop = t.scrollHeight; 
        }, 50);
    };

    commitBtn.addEventListener('click', addExoticBet);

    container.querySelector('#tab-direct').onclick = () => {
        appState.bettingMode = 'DIRECT';
        renderDashboard(cashierContext.state);
    };

    window.wizardProcess = () => {
        const { isValid } = calculateTotals();
        if (isValid) addExoticBet();
    };

    updateInstructions();
    renderGrid();
}

export function CashierPage() {
    const page = document.createElement('div');
    page.className = 'w-full h-screen flex flex-col bg-[#064e3b] overflow-hidden';

    appState.domElements.notificationContainerCashier = document.createElement('div');
    appState.domElements.notificationContainerCashier.className = 'notification-container';
    page.appendChild(appState.domElements.notificationContainerCashier);

    const header = CashierHeader();
    
    const hamburger = document.createElement('button');
    hamburger.id = 'mobile-hamburger-btn';
    hamburger.innerHTML = '☰';
    header.prepend(hamburger);
    const mobileCurrencyContainer = document.createElement('div');
    mobileCurrencyContainer.id = 'mobile-currency-container';
    header.appendChild(mobileCurrencyContainer);

    const sidebar = document.createElement('div');
    sidebar.id = 'mobile-sidebar';
    sidebar.className = 'mobile-sidebar';
    sidebar.innerHTML = `
        <div class="sidebar-close-overlay"></div>
        <div class="sidebar-content">
            <div class="sidebar-header">
                <h3>Menú de Cajero</h3>
                <button class="sidebar-close-btn">&times;</button>
            </div>
            <div class="sidebar-actions">
                <button id="side-btn-wa-promo" style="background:#128C7E; color:#fff; border-left:4px solid #25D366;">📢 Difundir Cartelera WA</button>
                <button id="side-btn-recon">💰 Cuadre de Caja</button>
                <button id="side-btn-tickets">📋 Listado de Tickets</button>
                <button id="side-btn-pay">💵 Pagar Premio</button>
                <button id="side-btn-void">🚫 Anular Ticket</button>
                <button id="side-btn-logout">🚪 Cerrar Sesión</button>
            </div>
        </div>
    `;
    page.appendChild(sidebar);

    const openSidebar = () => sidebar.classList.add('open');
    const closeSidebar = () => sidebar.classList.remove('open');

    hamburger.addEventListener('click', openSidebar);
    sidebar.querySelector('.sidebar-close-overlay').addEventListener('click', closeSidebar);
    sidebar.querySelector('.sidebar-close-btn').addEventListener('click', closeSidebar);

    sidebar.querySelector('#side-btn-wa-promo').addEventListener('click', () => { closeSidebar(); openWhatsAppPromoModal(); });
    sidebar.querySelector('#side-btn-recon').addEventListener('click', () => { closeSidebar(); showCashReconciliation(); });
    sidebar.querySelector('#side-btn-tickets').addEventListener('click', () => { closeSidebar(); showTicketList(); });
    sidebar.querySelector('#side-btn-pay').addEventListener('click', () => { closeSidebar(); openPayoutModal('Pagar Premio'); });
    sidebar.querySelector('#side-btn-void').addEventListener('click', () => { closeSidebar(); openPayoutModal('Anular Ticket'); });
    sidebar.querySelector('#side-btn-logout').addEventListener('click', () => { closeSidebar(); authService.logout(); window.location.href = '/login'; });

    document.body.addEventListener('click', (e) => {
        const btn = e.target.closest('button');
        if (!btn) return;

        // Evitar duplicados: Los botones de CashierHeader y Sidebar ya tienen sus propios listeners
        if (btn.id === 'globalPayoutBtn' || btn.id === 'globalVoidBtn' || btn.id === 'side-btn-pay' || btn.id === 'side-btn-void') {
            return;
        }

        if (btn.id === 'hdr-btn-pay' || (btn.textContent && btn.textContent.toUpperCase().includes('PAGAR PREMIO'))) {
            if(!document.querySelector('.payout-modal-overlay.visible')){
                openPayoutModal('Pagar Premio');
            }
        }
        else if (btn.id === 'hdr-btn-void' || (btn.textContent && btn.textContent.toUpperCase().includes('ANULAR'))) {
            if(!document.querySelector('.payout-modal-overlay.visible')){
                openPayoutModal('Anular Ticket');
            }
        }
    });

    page.appendChild(header);

    const dashboard = document.createElement('div');
    dashboard.className = 'pos-dashboard';
    page.appendChild(dashboard);
    let uiTimers = [];
    let isProcessingSale = false;

    // ⭐ CENTRO DE CAPTACIÓN Y DIFUSIÓN PARA GRUPOS DE WHATSAPP
    // ⭐ CENTRO DE CAPTACIÓN Y DIFUSIÓN PARA GRUPOS DE WHATSAPP
    function openWhatsAppPromoModal() {
        const state = cashierContext.state;
        if (!state.allRaces || state.allRaces.length === 0) {
            return Notification(appState.domElements.notificationContainerCashier, 'No hay carreras hoy para difundir.', 'warning');
        }

        const uniqueTracks = new Map();
        state.allRaces.forEach(r => {
            if (r.track_id && r.track_name) {
                if (!uniqueTracks.has(String(r.track_id))) {
                    uniqueTracks.set(String(r.track_id), { id: r.track_id, name: r.track_name, total: 0, open: 0, nextTime: null, races: [] });
                }
                const tk = uniqueTracks.get(String(r.track_id));
                tk.total++;
                tk.races.push(r);
                if (r.status === 'open' || r.status === 'scheduled') {
                    tk.open++;
                    const rTime = moment.utc(r.start_time);
                    if (!tk.nextTime || rTime.isBefore(tk.nextTime)) tk.nextTime = rTime;
                }
            }
        });

        // FIX: Ya no filtramos por "t.open > 0". Mostramos la cartelera completa del día.
        const tracksArr = Array.from(uniqueTracks.values()).sort((a, b) => a.name.localeCompare(b.name));
        const modal = document.createElement('div');
        modal.className = 'modal-overlay visible';
        modal.style.zIndex = '999999';

        const trackOptions = tracksArr.map(t => `<option value="${t.id}">${t.name} (${t.total} carreras hoy)</option>`).join('');

        modal.innerHTML = `
            <div class="modal-content" style="background:#022c22; border:3px solid #D4AF37; border-radius:16px; padding:20px; width:95%; max-width:480px; text-align:left;">
                <button class="close-btn" id="close-wa-promo" style="color:#D4AF37; font-size:1.5rem; float:right; background:transparent; border:none; cursor:pointer;">&times;</button>
                <h2 style="color:#D4AF37; font-size:1.4rem; margin-top:0; border-bottom:1px solid #047857; padding-bottom:10px;">📢 Captación por WhatsApp</h2>
                
                <div style="background:rgba(0,0,0,0.35); padding:14px; border-radius:10px; border:1px solid #14532d; margin-bottom:15px;">
                    <div style="color:#4ade80; font-weight:900; font-size:1rem; margin-bottom:8px;">1. Cartelera Completa del Día</div>
                    <p style="color:#cbd5e1; font-size:0.85rem; margin:0 0 12px 0;">Envía la lista de todos los hipódromos con carreras programadas para hoy.</p>
                    <div style="display:flex; gap:8px;">
                        <button id="btn-wa-text-schedule" style="flex:1; background:#128C7E; color:white; border:2px solid #25D366; padding:10px; border-radius:8px; font-weight:bold; cursor:pointer;">📲 Texto WA</button>
                        <button id="btn-pdf-schedule" style="flex:1; background:#D4AF37; color:black; border:none; padding:10px; border-radius:8px; font-weight:900; cursor:pointer;">📄 Descargar PDF</button>
                    </div>
                </div>

                <div style="background:rgba(0,0,0,0.35); padding:14px; border-radius:10px; border:1px solid #14532d;">
                    <div style="color:#facc15; font-weight:900; font-size:1rem; margin-bottom:8px;">2. Gaceta / Nómina por Hipódromo</div>
                    <label style="color:#a7f3d0; font-size:0.8rem; display:block; margin-bottom:5px;">Selecciona el Hipódromo:</label>
                    <select id="wa-promo-track-select" style="width:100%; padding:10px; background:#000; color:#fff; border:1px solid #D4AF37; border-radius:8px; margin-bottom:12px; font-weight:bold;">
                        ${trackOptions || '<option value="">Sin carreras programadas</option>'}
                    </select>
                    <div style="display:flex; gap:8px;">
                        <button id="btn-wa-text-gazette" style="flex:1; background:#128C7E; color:white; border:2px solid #25D366; padding:10px; border-radius:8px; font-weight:bold; cursor:pointer;">🐎 Nómina WA</button>
                        <button id="btn-pdf-gazette" style="flex:1; background:#0284c7; color:white; border:none; padding:10px; border-radius:8px; font-weight:900; cursor:pointer;">📘 Gaceta PDF</button>
                    </div>
                </div>
            </div>
        `;
        document.body.appendChild(modal);

        modal.querySelector('#close-wa-promo').onclick = () => modal.remove();

        modal.querySelector('#btn-wa-text-schedule').onclick = () => {
            const fechaHoy = moment().tz(VENEZUELA_TIMEZONE).format('DD/MM/YYYY');
            const lines = [
                `🏇 *CARTELERA HÍPICA OFICIAL* 🏇`,
                `📅 *Fecha:* ${fechaHoy}`,
                `━━━━━━━━━━━━━━━━━━━━`,
                `📍 *HIPÓDROMOS DE HOY:*`
            ];
            tracksArr.forEach(t => {
                const horaProx = t.nextTime ? t.nextTime.tz(VENEZUELA_TIMEZONE).format('hh:mm A') : '(Validar Horarios)';
                lines.push(`🟢 *${t.name}*`);
                lines.push(`   🏁 ${t.total} carreras hoy | ⏰ Inicio ref: ${horaProx}`);
            });
            lines.push(`━━━━━━━━━━━━━━━━━━━━`);
            lines.push(`📲 *¡Envía tu jugada por aquí y sella tu boleto al instante!*`);
            window.open(`https://wa.me/?text=${encodeURIComponent(lines.join('\n'))}`, '_blank');
        };

        modal.querySelector('#btn-pdf-schedule').onclick = () => {
            modal.remove();
            import('../utils/pdfGenerator.js').then(module => {
                module.generateDailySchedulePDF(state.allRaces, state.allTracks);
            });
        };

        modal.querySelector('#btn-wa-text-gazette').onclick = () => {
            const tkId = modal.querySelector('#wa-promo-track-select').value;
            if (!tkId) return;
            const tkObj = uniqueTracks.get(String(tkId));
            const racesOfTrack = tkObj.races.sort((a, b) => a.race_number - b.race_number);

            const lines = [
                `📘 *PROGRAMA OFICIAL - ${tkObj.name.toUpperCase()}*`,
                `━━━━━━━━━━━━━━━━━━━━`
            ];

            racesOfTrack.forEach(r => {
                const hora = moment.utc(r.start_time).tz(VENEZUELA_TIMEZONE).format('hh:mm A');
                lines.push(`\n🏁 *CARRERA #${r.race_number}* (${hora})`);
                const runners = (r.runners || []).sort((a, b) => a.horse_number - b.horse_number);
                runners.forEach(h => {
                    if (h.is_scratched) {
                        lines.push(`  ❌ ~#${h.horse_number} ${h.name}~`);
                    } else {
                        lines.push(`  🐎 *#${h.horse_number}* - ${h.name}`);
                    }
                });
            });

            lines.push(`\n━━━━━━━━━━━━━━━━━━━━`);
            lines.push(`💵 *Responde este mensaje con Hipódromo, Carrera, # Ejemplar y Monto para sellar tu ticket.*`);
            window.open(`https://wa.me/?text=${encodeURIComponent(lines.join('\n'))}`, '_blank');
        };

        modal.querySelector('#btn-pdf-gazette').onclick = () => {
            const tkId = modal.querySelector('#wa-promo-track-select').value;
            if (!tkId) return;
            const tkObj = state.allTracks.find(t => String(t.id) === String(tkId));
            const racesOfTrack = state.allRaces.filter(r => String(r.track_id) === String(tkId)).sort((a, b) => a.race_number - b.race_number);
            
            modal.remove();
            import('../utils/pdfGenerator.js').then(module => {
                module.generateGazettePDF(tkObj, racesOfTrack);
            });
        };
    }

    async function softReload(isAfterSale = false) {
        cashierContext.update({ bets: [] });
        await initializePage(isAfterSale);
        appState.keyboardMode = 'MATRIX';
        setTimeout(() => {
            const firstInput = document.querySelector('.matrix-input');
            if (firstInput) { firstInput.focus(); firstInput.select(); }
        }, 100);
    }

    const renderDashboard = (state) => {
        dashboard.innerHTML = ''; 
        // ⭐ FIX MÓVIL: Respetar el flujo natural (Tracks -> Races -> Matrix) en lugar de forzar Matrix
        dashboard.setAttribute('data-mobile-step', state.mobileStep || 'tracks');
        uiTimers.forEach(t => clearInterval(t)); uiTimers = [];

        if (state.allRaces.length === 0) {
            dashboard.innerHTML = `
                <div style="margin: auto; text-align: center;">
                    <i class="fas fa-calendar-times" style="font-size: 4rem; color: #D4AF37; margin-bottom: 20px;"></i>
                    <h2 style="color:#D4AF37; font-size: 2.5rem; font-weight: 900;">NO HAY CARRERAS PROGRAMADAS</h2>
                    <p style="color:#a7f3d0; font-size: 1.2rem; margin-top: 10px;">Para hoy: ${moment().tz(VENEZUELA_TIMEZONE).format('DD/MM/YYYY')}</p>
                </div>
            `;
            return;
        }

        const currencySym = state.availableCurrencies?.find(c => c.code === state.currency)?.symbol || '$';
        const mobileCurrDiv = document.getElementById('mobile-currency-container');
        if (mobileCurrDiv) {
            mobileCurrDiv.innerHTML = `
                <select id="mobile-currency-select" style="background:#022c22; color:#D4AF37; border:1px solid #D4AF37; padding:6px 10px; border-radius:6px; font-weight:bold; font-size:1rem; cursor:pointer;" ${(state.userCurrency && state.userCurrency !== 'BOTH') ? 'disabled' : ''}>
                    ${(state.availableCurrencies || [])
                        .filter(c => !state.userCurrency || (state.userCurrency === 'BOTH' && (c.code === 'USD' || c.code === 'VES')) || c.code === state.userCurrency)
                        .map(c => `<option value="${c.code}" ${state.currency === c.code ? 'selected' : ''}>${c.code} (${c.symbol})</option>`).join('')}
                </select>
            `;
            const mSelect = mobileCurrDiv.querySelector('#mobile-currency-select');
            if (mSelect) {
                mSelect.addEventListener('change', (e) => {
                    if (state.userCurrency && state.userCurrency !== 'BOTH') return; // Bloqueo de seguridad
                    localStorage.setItem('cashier_currency', e.target.value); 
                    cashierContext.update({ currency: e.target.value }); 
                });
            }
        }
        // --- COL 1: HIPÓDROMOS Y CARRERAS ---
        const col1 = document.createElement('div');
        col1.className = 'pos-panel';
        
        const uniqueTracks = new Map();
        state.allRaces.forEach(r => {
            if (r.track_id && r.track_name) {
                uniqueTracks.set(String(r.track_id), { id: r.track_id, name: r.track_name });
            }
        });

        const sortedTracks = Array.from(uniqueTracks.values()).sort((tA, tB) => {
            const getNextRaceTime = (trackId) => {
                const races = state.allRaces.filter(r => String(r.track_id) === String(trackId) && (r.status === 'open' || r.status === 'scheduled'));
                races.sort((a,b) => moment.utc(a.start_time).diff(moment.utc(b.start_time)));
                return races.length > 0 ? moment.utc(races[0].start_time).valueOf() : Infinity; 
            };
            return getNextRaceTime(tA.id) - getNextRaceTime(tB.id);
        });

        appState.tracksByLetter = {};
        sortedTracks.forEach(t => {
            const letter = t.name.charAt(0).toLowerCase();
            if (!appState.tracksByLetter[letter]) appState.tracksByLetter[letter] = [];
            appState.tracksByLetter[letter].push(t);
        });
        
        const trackFocusGlow = appState.keyboardMode === 'TRACKS' ? 'box-shadow: inset 0 0 0 3px #facc15;' : '';
        let tracksHtml = `<div class="panel-title">1. HIPÓDROMO <span style="font-size:0.8rem; float:right; color:#4ade80;">[I]</span></div>
                          <div class="track-list-container" id="track-list-box" style="${trackFocusGlow}">`;
        
        sortedTracks.forEach((t, i) => {
            const isSelected = state.selectedTrack && String(state.selectedTrack.id) === String(t.id);
            const tRaces = state.allRaces.filter(r => String(r.track_id) === String(t.id));
            const nRace = tRaces.filter(r => r.status === 'scheduled' || r.status === 'open').sort((a,b) => moment.utc(a.start_time).diff(moment.utc(b.start_time)))[0];
            const mtpText = nRace ? `<span class="track-timer text-xs text-yellow-400 font-mono" data-time="${nRace.start_time}"></span>` : '<span class="text-xs text-gray-400">FIN</span>';

            tracksHtml += `<button tabindex="0" class="track-btn ${isSelected ? 'selected' : ''}" data-id="${t.id}" data-idx="${i+1}">
                <div style="text-align:left; overflow:hidden; text-overflow:ellipsis;">
                    <div style="font-size: 1.1rem; white-space:nowrap;">${t.name}</div>
                    ${mtpText}
                </div>
                <span style="font-size:0.8rem; background:rgba(0,0,0,0.5); padding:2px 6px; border-radius:4px; color:#4ade80;">[${i+1}]</span>
            </button>`;
        });
        tracksHtml += '</div>';

        const raceFocusGlow = appState.keyboardMode === 'RACES' ? 'box-shadow: inset 0 0 0 3px #facc15;' : '';
        let racesHtml = `
            <div class="mobile-step-header">
                <button class="mobile-back-btn" id="back-to-tracks">← Volver a Hipódromos</button>
            </div>
            <div class="panel-title" style="background:#047857;">2. CARRERA <span style="font-size:0.8rem; float:right; color:#4ade80;">[*]</span></div>
            <div class="race-grid-container" id="race-list-box" style="${raceFocusGlow}">`;
        
        if (state.selectedTrack) {
            const trackRaces = state.allRaces.filter(r => String(r.track_id) === String(state.selectedTrack.id)).sort((a, b) => a.race_number - b.race_number);
            trackRaces.forEach((r, idx) => {
                const isSelected = state.selectedRace && String(state.selectedRace.id) === String(r.id);
                const isOpen = r.status === 'open' || r.status === 'scheduled';
                const isFinished = r.status === 'finished';
                const isClosed = !isOpen && !isFinished;
                
                const btnClass = isOpen ? '' : (isFinished ? 'finished' : 'disabled closed-race');
                const badgeColor = isOpen ? '#4ade80' : (isFinished ? '#fff' : '#ef4444');
                const badgeText = isOpen ? 'ACT' : (isFinished ? 'RES' : 'CER');

                racesHtml += `<button ${isClosed ? 'disabled="true"' : ''} tabindex="${isClosed ? '-1' : '0'}" class="race-btn ${isSelected ? 'selected' : ''} ${btnClass}" data-id="${r.id}" data-idx="${idx+1}">
                    <div style="width:100%; display:flex; justify-content:space-between; align-items:flex-start;">
                        <span style="font-size:0.8rem; color:#ccc;">[${idx+1}]</span>
                        <span style="font-size:0.75rem; color:${badgeColor}; font-weight:bold;">${badgeText}</span>
                    </div>
                    <span style="font-size:1.6rem; font-weight:900;">C-${r.race_number}</span>
                </button>`;
            });
        }
        racesHtml += '</div>';
        col1.innerHTML = `<div class="pos-col1-wrapper">${tracksHtml}${racesHtml}</div>`;
        dashboard.appendChild(col1);

        // --- COL 2: MATRIZ ---
        const col2 = document.createElement('div');
        col2.className = 'pos-panel';
        
        const raceStatus = state.selectedRace ? state.selectedRace.status : null;
        const raceActive = (raceStatus === 'open' || raceStatus === 'scheduled');
        const raceFinished = (raceStatus === 'finished');
        
        let centerHtml = `
            <div class="mobile-step-header" style="display: none;">
                <button class="mobile-back-btn" id="back-to-races">← Volver a Carreras</button>
            </div>
            <div class="panel-title" style="background:#15803d; color:white;">3. ARMAR JUGADA</div>
            <div class="center-betting-area" id="center-dynamic-area">`;
        
        if (!state.selectedRace) {
            centerHtml += `<div class="lock-screen" style="background:rgba(2,44,34,0.9);"><i class="fas fa-arrow-left lock-screen-icon"></i> Elija Carrera</div>`;
        } else if (raceFinished) {
            centerHtml += `<div id="results-loader" style="text-align:center; color:#D4AF37; margin-top:50px; font-size:1.5rem;">⏳ Cargando dividendos oficiales...</div>`;
            setTimeout(() => loadAndRenderFinishedRace(state.selectedRace, state, appState), 50);
        } else if (!raceActive) {
            centerHtml += `
                <div style="display:flex; justify-content:space-between; align-items:flex-end; border-bottom: 2px dashed #047857; padding-bottom:10px; margin-bottom: 10px;">
                    <div style="flex:1;">
                        <div style="color: #4ade80; font-size: 1.8rem; font-weight: 900; line-height: 1.1; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${state.selectedTrack.name}</div>
                        <div style="color: #fff; font-size: 1.5rem; font-weight: bold;">CARRERA ${state.selectedRace.race_number}</div>
                    </div>
                    <div style="text-align: right;">
                        <div style="color: #ef4444; font-size: 2rem; font-weight: 900;">CERRADA</div>
                        <div style="color: #a7f3d0; font-size: 0.9rem;">ESPERANDO RESULTADOS OFICIALES...</div>
                    </div>
                </div>
            `;
            
            let matrixHtml = `<div class="bet-matrix-container" style="border-radius: 0 0 8px 8px; border-top:none; opacity: 0.5;"><table class="bet-matrix">
                <thead><tr><th style="width:50px;">N°</th><th style="text-align:left;">EJEMPLAR</th><th>WIN</th><th>PLACE</th><th>SHOW</th></tr></thead><tbody>`;
            
            const run = state.selectedRace.runners || [];
            if(run.length === 0) matrixHtml += `<tr><td colspan="5" style="text-align:center; padding:20px; color:#facc15;">No hay caballos disponibles en el sistema para esta carrera.</td></tr>`;
            
            run.forEach((h) => {
                if (h.is_scratched) {
                    matrixHtml += `<tr><td style="font-weight:900; color:#ef4444; font-size:1.3rem;">#${h.horse_number}</td><td style="text-align:left; color:#ef4444; text-decoration:line-through;">${h.name}</td><td colspan="3" style="color:#ef4444; font-weight:bold;">RETIRADO</td></tr>`;
                } else {
                    const cInfo = getHorseColor(h.horse_number); 
                    matrixHtml += `<tr>
                        <td style="font-weight:900; color:${cInfo.text}; background-color:${cInfo.bg}; font-size:1.3rem; border: 1px solid #000;">#${h.horse_number}</td>
                        <td style="text-align:left; font-weight:bold; color:white; font-size:1.1rem; white-space:nowrap; overflow:hidden;">${h.name}</td>
                        <td>---</td><td>---</td><td>---</td>
                    </tr>`;
                }
            });
            matrixHtml += `</tbody></table></div>`;
            centerHtml += matrixHtml; 

        }
        else if (appState.bettingMode === 'EXOTIC') {
            // ⭐ INYECCIÓN DE LA NUEVA MATRIZ TABULAR PARA EXÓTICAS
            centerHtml += `
                <div style="display:flex; justify-content:space-between; align-items:flex-end; border-bottom: 2px dashed #047857; padding-bottom:10px; margin-bottom: 10px; flex-shrink: 0;">
                    <div style="flex:1;">
                        <div style="color: #4ade80; font-size: 1.8rem; font-weight: 900; line-height: 1.1; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${state.selectedTrack.name}</div>
                        <div style="color: #fff; font-size: 1.5rem; font-weight: bold;">CARRERA ${state.selectedRace.race_number}</div>
                    </div>
                    <div style="text-align: right;">
                        <div id="center-huge-timer" data-time="${state.selectedRace.start_time}" style="color: #facc15; font-size: 2.2rem; font-weight: 900; font-family: monospace;"></div>
                        <div style="color: #a7f3d0; font-size: 0.9rem;">MINUTOS PARA EL CIERRE</div>
                    </div>
                </div>
                
                <!-- ⭐ BOTONES ESTILIZADOS PARA NAVEGACIÓN -->
                <div class="bet-mode-selector" style="display: flex; gap: 10px; margin-bottom: 10px; flex-shrink: 0;">
                    <button id="mode-direct" style="flex:1; padding: 10px; font-size: 1rem; font-weight: bold; border-radius: 8px; cursor: pointer; border: 2px solid #047857; background: #022c22; color: #94a3b8; transition: all 0.2s;" onmouseover="this.style.background='#047857'; this.style.color='white'" onmouseout="this.style.background='#022c22'; this.style.color='#94a3b8'">
                        <i class="fas fa-arrow-left"></i> VOLVER A DIRECTAS [F1]
                    </button>
                    <button id="mode-exotic" style="flex:1; padding: 10px; font-size: 1rem; font-weight: bold; border-radius: 8px; cursor: pointer; border: 2px solid #D4AF37; background: #D4AF37; color: black; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">
                        🎡 MODO EXÓTICAS [F2]
                    </button>
                </div>
                
                <div id="exotic-wizard-container" class="exotic-wizard-pos"></div>
            `;
            setTimeout(() => {
                // Aquí construimos el interior del contenedor en vivo para no perder el contexto
                const exoContainer = document.getElementById('exotic-wizard-container');
                if (!exoContainer) return;
                
                const currencyObj = state.availableCurrencies.find(c => c.code === state.currency);
                const currencySym = currencyObj ? currencyObj.symbol : '$';
                const activeHorses = (state.selectedRace.runners || []).filter(r => !r.is_scratched);
                
                const betTypeDefs = {
                    'EXACTA': { pos: 2, name: 'Exacta', defBase: 1.00 },
                    'TRIFECTA': { pos: 3, name: 'Trifecta', defBase: 0.50 },
                    'SUPERFECTA': { pos: 4, name: 'Superfecta', defBase: 0.10 },
                    'DAILY_DOUBLE': { pos: 2, name: 'Daily Double', defBase: 2.00 },
                    'PICK_3': { pos: 3, name: 'Pick 3', defBase: 0.50 }
                };

                const wizardState = { selections: [new Set()] };

                exoContainer.innerHTML = `
                    <div style="display:flex; gap:10px; margin-bottom:10px; flex-shrink:0;">
                        <select id="exo-type-select" style="flex:1; background:#022c22; color:#4ade80; border:1px solid #D4AF37; padding:6px; border-radius:5px; font-weight:bold; font-size:0.95rem; outline:none; cursor:pointer;">
                            <option value="EXACTA">Exacta (1-2)</option>
                            <option value="TRIFECTA" selected>Trifecta (1-2-3)</option>
                            <option value="SUPERFECTA">Superfecta (1-2-3-4)</option>
                            <option value="DAILY_DOUBLE">Daily Double</option>
                            <option value="PICK_3">Pick 3</option>
                        </select>
                        <select id="exo-mode-select" style="flex:1; background:#022c22; color:#4ade80; border:1px solid #D4AF37; padding:6px; border-radius:5px; font-weight:bold; font-size:0.95rem; outline:none; cursor:pointer;">
                            <option value="CAJA" selected>Candado (Box)</option>
                            <option value="RUEDA">En Rueda</option>
                        </select>
                        <button id="btn-exo-guide" style="background:#D4AF37; color:black; font-weight:bold; border-radius:50%; width:30px; height:30px; border:none; cursor:pointer; flex-shrink:0; box-shadow: 0 2px 4px rgba(0,0,0,0.5);" title="Guía">❓</button>
                    </div>
                    
                    <div id="exo-instruction-banner" style="background: rgba(16, 185, 129, 0.1); border-left: 4px solid #10b981; padding: 6px; font-size: 0.8rem; color: #a7f3d0; margin-bottom: 10px; line-height: 1.2; flex-shrink:0;">
                        📌 <b>CANDADO (BOX):</b> Selecciona 3 o más ejemplares.
                    </div>
                    
                    <div class="bet-matrix-container" style="flex: 1 1 auto; overflow-y:auto; overflow-x:auto; margin-bottom:10px; border: 1px solid #047857; border-radius: 8px; background: #000; min-height: 150px; max-height: 230px;">
                        <table class="bet-matrix" id="exo-grid-table" style="width:100%;">
                        </table>
                    </div>

                    <div style="display:flex; justify-content:space-between; align-items:center; background:#022c22; padding:10px; border-radius:8px; border:1px solid #D4AF37; margin-bottom:10px; flex-shrink:0;">
                        <div>
                            <label style="color:#94a3b8; font-size:0.75rem; font-weight:bold; display:block; margin-bottom:2px;">Base Oficial</label>
                            <div style="display:flex; align-items:center; gap:5px;">
                                <span style="color:#10b981; font-size:1rem; font-weight:bold;">${currencySym}</span>
                                <input type="number" id="exo-base-input" value="0.50" step="0.10" style="width:65px; background:#000; color:white; border:1px solid #334155; padding:4px; border-radius:4px; font-weight:bold; font-size:0.95rem; text-align:center; outline:none;">
                            </div>
                        </div>
                        <div style="text-align:right;">
                            <div id="exo-combs-display" style="color:#a7f3d0; font-size:0.85rem; font-weight:bold;">0 comb.</div>
                            <div id="exo-total-display" style="color:#D4AF37; font-weight:900; font-size:1.4rem; line-height:1;">${currencySym}0.00</div>
                        </div>
                    </div>
                    
                    <button id="btn-commit-exotic" style="background:#15803d; color:white; border:2px solid #4ade80; padding:10px; font-size:1rem; font-weight:bold; border-radius:8px; cursor:not-allowed; flex-shrink:0; transition:all 0.2s; opacity:0.4; width:100%;" disabled>
                        ⚠️ COMPLETE SELECCIÓN
                    </button>
                `;

                const typeSelect = exoContainer.querySelector('#exo-type-select');
                const modeSelect = exoContainer.querySelector('#exo-mode-select');
                const baseInput = exoContainer.querySelector('#exo-base-input');
                const tableEl = exoContainer.querySelector('#exo-grid-table');
                const combsDisplay = exoContainer.querySelector('#exo-combs-display');
                const totalDisplay = exoContainer.querySelector('#exo-total-display');
                const commitBtn = exoContainer.querySelector('#btn-commit-exotic');
                const bannerEl = exoContainer.querySelector('#exo-instruction-banner');
                const btnExoGuide = exoContainer.querySelector('#btn-exo-guide');

                const updateInstructions = () => {
                    const type = typeSelect.value;
                    const mode = modeSelect.value;
                    const posCount = betTypeDefs[type].pos;
                    if (mode === 'CAJA') {
                        bannerEl.innerHTML = `📌 <b>CANDADO (BOX):</b> Selecciona <b>${posCount} o más</b> ejemplares. Todas las combinaciones posibles quedarán cubiertas.`;
                    } else {
                        bannerEl.innerHTML = `📌 <b>EN RUEDA:</b> Selecciona al menos 1 ejemplar para <b>CADA</b> posición obligatoria (del 1ro al ${posCount}do).`;
                    }
                };

                btnExoGuide.onclick = () => {
                    const modal = document.createElement('div');
                    modal.className = 'modal-overlay visible';
                    modal.style.zIndex = '100000';
                    modal.innerHTML = `
                        <div class="modal-content" style="background:#1e293b; border:2px solid #D4AF37; text-align:left; max-width: 500px; padding:25px;">
                            <h3 style="color:#D4AF37; font-size:1.3rem; margin-top:0; border-bottom:1px solid #334155; padding-bottom:10px;"><i class="fas fa-book"></i> Guía de Exóticas</h3>
                            <h4 style="color:#4ade80; margin-bottom:5px;">🔒 CANDADO (BOX)</h4>
                            <p style="color:#cbd5e1; font-size:0.85rem; margin-top:0; margin-bottom:15px; line-height:1.4;">Cubre todas las combinaciones posibles entre los caballos seleccionados sin importar el orden.</p>
                            <h4 style="color:#60a5fa; margin-bottom:5px;">🎡 EN RUEDA</h4>
                            <p style="color:#cbd5e1; font-size:0.85rem; margin-top:0; margin-bottom:15px; line-height:1.4;">Debes seleccionar caballos específicos para el 1er lugar, 2do lugar, etc.</p>
                            <button class="btn-huge-primary" style="width:100%; font-size:1rem; padding:10px;" onclick="this.closest('.modal-overlay').remove()">Entendido</button>
                        </div>
                    `;
                    document.body.appendChild(modal);
                };

                const renderGrid = () => {
                    const type = typeSelect.value;
                    const mode = modeSelect.value;
                    const posCount = betTypeDefs[type].pos;

                    if (mode === 'CAJA' && wizardState.selections.length !== 1) {
                        wizardState.selections = [new Set()];
                    } else if (mode === 'RUEDA' && wizardState.selections.length !== posCount) {
                        wizardState.selections = Array.from({length: posCount}, () => new Set());
                    }

                    let html = '<thead style="position:sticky; top:0; z-index:2; background:#064e3b; box-shadow: 0 2px 4px rgba(0,0,0,0.5);"><tr><th style="padding:4px; border:1px solid #334155; min-width:50px;">Pos \\ Ej</th>';
                    
                    activeHorses.forEach(h => {
                        const color = getHorseColor(h.horse_number);
                        html += `<th style="padding:4px; border:1px solid #334155; min-width:35px;">
                                    <div style="background:${color.bg}; color:${color.text}; border-radius:3px; padding:2px; font-weight:bold;">${h.horse_number}</div>
                                 </th>`;
                    });
                    html += '</tr></thead><tbody>';

                    if (activeHorses.length === 0) {
                        html += '<tr><td colspan="20" style="text-align:center; padding:20px; color:#facc15;">No hay caballos disponibles.</td></tr>';
                    } else {
                        if (mode === 'CAJA') {
                            html += `<tr>`;
                            html += `<td style="padding:4px; border:1px solid #334155; font-weight:bold; background:#1e293b; color:#94a3b8;">SEL</td>`;
                            activeHorses.forEach((h, hIdx) => {
                                const isSelected = wizardState.selections[0].has(hIdx);
                                html += `<td style="padding:4px; border:1px solid #334155;">
                                            <div class="exo-matrix-cell ${isSelected ? 'selected' : ''}" data-row="0" data-hidx="${hIdx}">✓</div>
                                         </td>`;
                            });
                            html += `</tr>`;
                        } else {
                            for(let r = 0; r < posCount; r++) {
                                html += `<tr>`;
                                html += `<td style="padding:4px; border:1px solid #334155; font-weight:bold; background:#1e293b; color:#94a3b8;">${r+1}ro</td>`;
                                activeHorses.forEach((h, hIdx) => {
                                    const isSelected = wizardState.selections[r].has(hIdx);
                                    html += `<td style="padding:4px; border:1px solid #334155;">
                                                <div class="exo-matrix-cell ${isSelected ? 'selected' : ''}" data-row="${r}" data-hidx="${hIdx}">✓</div>
                                             </td>`;
                                });
                                html += `</tr>`;
                            }
                        }
                    }
                    html += '</tbody>';
                    tableEl.innerHTML = html;

                    tableEl.querySelectorAll('.exo-matrix-cell').forEach(cell => {
                        cell.addEventListener('click', () => {
                            const r = parseInt(cell.dataset.row);
                            const hIdx = parseInt(cell.dataset.hidx);
                            if (wizardState.selections[r].has(hIdx)) wizardState.selections[r].delete(hIdx);
                            else wizardState.selections[r].add(hIdx);
                            renderGrid();
                        });
                    });
                    calculateTotals();
                };

                const calculateTotals = () => {
                    const type = typeSelect.value;
                    const mode = modeSelect.value;
                    const posCount = betTypeDefs[type].pos;
                    const baseCost = parseFloat(baseInput.value) || 0;
                    let combs = 0;
                    let isValid = false;

                    if (mode === 'CAJA') {
                        const n = wizardState.selections[0].size;
                        if (n >= posCount) {
                            isValid = true;
                            combs = 1;
                            for (let i = 0; i < posCount; i++) combs *= (n - i);
                        }
                    } else {
                        const allFilled = wizardState.selections.every(set => set.size > 0);
                        if (allFilled) {
                            const getCombs = (idx = 0, current = []) => {
                                if (idx === posCount) { combs++; return; }
                                for (const h of wizardState.selections[idx]) {
                                    if (!current.includes(h)) getCombs(idx + 1, [...current, h]);
                                }
                            };
                            getCombs();
                            if (combs > 0) isValid = true;
                        }
                    }

                    const total = combs * baseCost;
                    combsDisplay.textContent = `${combs} comb.`;
                    totalDisplay.textContent = `${currencySym}${total.toFixed(2)}`;

                    if (isValid && combs > 0 && baseCost > 0) {
                        commitBtn.disabled = false;
                        commitBtn.style.opacity = '1';
                        commitBtn.style.cursor = 'pointer';
                        commitBtn.innerHTML = '➕ AGREGAR AL TICKET';
                    } else {
                        commitBtn.disabled = true;
                        commitBtn.style.opacity = '0.5';
                        commitBtn.style.cursor = 'not-allowed';
                        commitBtn.innerHTML = '⚠️ COMPLETE SELECCIÓN';
                    }
                    return { combs, cost: total, isValid };
                };

                typeSelect.addEventListener('change', () => { baseInput.value = betTypeDefs[typeSelect.value].defBase.toFixed(2); updateInstructions(); renderGrid(); });
                modeSelect.addEventListener('change', () => { updateInstructions(); renderGrid(); });
                baseInput.addEventListener('input', calculateTotals);

                const addExoticBet = () => {
                    const { combs, cost, isValid } = calculateTotals();
                    if (!isValid || combs === 0 || cost <= 0) return;

                    const mode = modeSelect.value;
                    const type = typeSelect.value;
                    const posCount = betTypeDefs[type].pos;
                    
                    const selectionsObj = {};
                    if (mode === 'CAJA') {
                        for (let r = 0; r < posCount; r++) { wizardState.selections[0].forEach(hIdx => { selectionsObj[`${r}_${hIdx}`] = true; }); }
                    } else {
                        for (let r = 0; r < posCount; r++) { wizardState.selections[r].forEach(hIdx => { selectionsObj[`${r}_${hIdx}`] = true; }); }
                    }

                    const betDetails = {
                        type: type,
                        selections: selectionsObj,
                        runners: activeHorses.map(r => ({ id: r.id, horse_number: r.horse_number, name: r.name })),
                        isBox: mode === 'CAJA',
                        combinations: combs,
                        multiplier: 1, 
                        quantity: 1,
                        cost: cost
                    };

                    const newBet = { 
                        type: 'EXOTIC', 
                        details: betDetails, 
                        horseId: 0, 
                        horseNumber: 'EXO', 
                        horseName: `${type} ${mode === 'CAJA' ? 'BOX' : 'RUEDA'}`, 
                        win: cost, place: 0, show: 0 
                    };
                    
                    cashierContext.update({ bets: [...cashierContext.state.bets, newBet] });
                    
                    wizardState.selections = mode === 'CAJA' ? [new Set()] : Array.from({length: posCount}, () => new Set());
                    renderGrid();
                    setTimeout(() => { const t = document.getElementById('ticket-scroll-area'); if(t) t.scrollTop = t.scrollHeight; }, 50);
                };

                commitBtn.addEventListener('click', addExoticBet);

                // Integración del teclado para el enter
                window.wizardProcess = () => {
                    const { isValid } = calculateTotals();
                    if (isValid) addExoticBet();
                };

                updateInstructions();
                renderGrid();
            }, 0); 
        } else {
            const headerHtml = `
                <div style="display:flex; justify-content:space-between; align-items:flex-end; border-bottom: 2px dashed #047857; padding-bottom:10px; margin-bottom: 10px;">
                    <div style="flex:1;">
                        <div style="color: #4ade80; font-size: 1.8rem; font-weight: 900; line-height: 1.1; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${state.selectedTrack.name}</div>
                        <div style="color: #fff; font-size: 1.5rem; font-weight: bold;">CARRERA ${state.selectedRace.race_number}</div>
                    </div>
                    <div style="text-align: right;">
                        <div id="center-huge-timer" data-time="${state.selectedRace.start_time}" style="color: #facc15; font-size: 2.2rem; font-weight: 900; font-family: monospace;"></div>
                        <div style="color: #a7f3d0; font-size: 0.9rem;">MINUTOS PARA EL CIERRE</div>
                    </div>
                </div>
                
                <!-- ⭐ BOTONES ESTILIZADOS PARA NAVEGACIÓN EN APUESTAS DIRECTAS -->
                <div class="bet-mode-selector" style="display: flex; gap: 10px; margin-bottom: 15px; flex-shrink: 0;">
                    <button id="mode-direct" style="flex:1; padding: 10px; font-size: 1rem; font-weight: bold; border-radius: 8px; cursor: pointer; border: 2px solid #D4AF37; background: #D4AF37; color: black; box-shadow: 0 2px 4px rgba(0,0,0,0.3);">
                        🎯 DIRECTAS [F1]
                    </button>
                    <button id="mode-exotic" style="flex:1; padding: 10px; font-size: 1rem; font-weight: bold; border-radius: 8px; cursor: pointer; border: 2px solid #047857; background: #022c22; color: #94a3b8; box-shadow: 0 2px 4px rgba(0,0,0,0.3); transition:all 0.2s;" onmouseover="this.style.background='#047857'; this.style.color='white'" onmouseout="this.style.background='#022c22'; this.style.color='#94a3b8'">
                        🎡 EXÓTICAS [F2]
                    </button>
                </div>
            `;
            centerHtml += headerHtml;
            
            const runners = state.selectedRace.runners || [];
            
            // ⭐ NUEVO POKA-YOKE: Detector Dinámico de Multitud (> 10 Caballos)
            const isDense = runners.length > 10;
            
            // Inyectamos la clase 'dense-matrix' dinámicamente si aplica
            let matrixHtml = `<div class="bet-matrix-container ${isDense ? 'dense-matrix' : ''}"><table class="bet-matrix">
                <thead><tr><th style="width:50px;">N°</th><th style="text-align:left;">EJEMPLAR</th><th>WIN [W]</th><th>PLACE [P]</th><th>SHOW [S]</th></tr></thead><tbody>`;
            
            if(runners.length === 0) {
                matrixHtml += `<tr><td colspan="5" style="text-align:center; padding:20px; color:#facc15;">No hay caballos disponibles en el sistema para esta carrera.</td></tr>`;
            }
            
            let rIdx = 0; 
            runners.forEach(h => {
                if (h.is_scratched) {
                    matrixHtml += `<tr><td style="font-weight:900; color:#ef4444; font-size:${isDense ? '1.1rem' : '1.3rem'};">#${h.horse_number}</td><td style="text-align:left; color:#ef4444; text-decoration:line-through; font-size:${isDense ? '1rem' : '1.1rem'};">${h.name}</td><td colspan="3" style="color:#ef4444; font-weight:bold;">RETIRADO</td></tr>`;
                } else {
                    const cInfo = getHorseColor(h.horse_number); 
                    matrixHtml += `<tr>
                        <td style="font-weight:900; color:${cInfo.text}; background-color:${cInfo.bg}; font-size:${isDense ? '1.1rem' : '1.3rem'}; border: 1px solid #000;">#${h.horse_number}</td>
                        <td style="text-align:left; font-weight:bold; color:white; font-size:${isDense ? '1rem' : '1.1rem'}; white-space:nowrap; overflow:hidden;">${h.name}</td>
                        <td><input type="number" class="matrix-input" data-row="${rIdx}" data-col="0" data-hid="${h.id}" data-hnum="${h.horse_number}" data-hname="${h.name}"></td>
                        <td><input type="number" class="matrix-input" data-row="${rIdx}" data-col="1" data-hid="${h.id}" data-hnum="${h.horse_number}" data-hname="${h.name}"></td>
                        <td><input type="number" class="matrix-input" data-row="${rIdx}" data-col="2" data-hid="${h.id}" data-hnum="${h.horse_number}" data-hname="${h.name}"></td>
                    </tr>`;
                    rIdx++;
                }
            });
            matrixHtml += `</tbody></table></div>`;
            centerHtml += matrixHtml;
        }
        
        centerHtml += '</div>';
        col2.innerHTML = centerHtml;
        dashboard.appendChild(col2);

        // --- COL 3: TICKET ---
        const col3 = document.createElement('div');
        col3.className = 'pos-panel';
        
        let grandTotal = 0;
        state.bets.forEach(b => grandTotal += (b.win + b.place + b.show));
        
        const currencySelectHtml = `
            <div style="background:#000; border-bottom: 2px solid #D4AF37; padding: 10px 15px; display:flex; justify-content:space-between; align-items:center;">
                <span style="color:#D4AF37; font-weight:900; font-size:1.1rem; text-transform:uppercase;">Moneda:</span>
                <select id="pos-currency-select" style="background:#022c22; color:#4ade80; border:1px solid #D4AF37; padding:5px 10px; border-radius:5px; font-weight:bold; font-size:1.1rem; cursor:pointer;" ${(state.userCurrency && state.userCurrency !== 'BOTH') ? 'disabled' : ''}>
                    ${(state.availableCurrencies || [])
                        .filter(c => !state.userCurrency || (state.userCurrency === 'BOTH' && (c.code === 'USD' || c.code === 'VES')) || c.code === state.userCurrency) 
                        .map(c => `<option value="${c.code}" ${state.currency === c.code ? 'selected' : ''}>${c.code} (${c.symbol})</option>`).join('')}
                </select>
            </div>
        `;

        let ticketHtml = '';
        if (state.bets.length === 0) {
            ticketHtml = '<div style="text-align:center; padding:40px; color:#64748b; font-weight:bold; font-size:1.2rem;">TICKET VACÍO<br><br>Llene la matriz y presione ENTER</div>';
        } else {
            const currentSymbol = state.availableCurrencies?.find(c => c.code === state.currency)?.symbol || '$';
            state.bets.forEach(b => {
                if (b.type === 'EXOTIC') {
                    const { type, selections, runners, isBox, combinations, cost } = b.details;
                    const betPositions = { 'EXACTA': 2, 'TRIFECTA': 3, 'SUPERFECTA': 4 };
                    const numPositions = betPositions[type] || 0;

                    const selectionsByRow = {};
                    for (const key in selections) {
                        const [row, col] = key.split('_').map(Number);
                        if (!selectionsByRow[row]) {
                            selectionsByRow[row] = [];
                        }
                        const horseNumber = runners[col]?.horse_number;
                        if (horseNumber) {
                            selectionsByRow[row].push(horseNumber);
                        }
                    }

                    const rowTexts = [];
                    for (let i = 0; i < numPositions; i++) {
                        const horsesInRow = selectionsByRow[i] || [];
                        rowTexts.push(horsesInRow.sort((a, b) => a - b).join(','));
                    }
                    const selectionsText = rowTexts.join(' / ');
                    const betTitle = `${type} ${isBox ? 'BOX' : ''}`;

                    ticketHtml += `
                        <div class="ticket-item">
                            <div class="item-info">
                                <span style="font-size:1.2rem; font-weight:900; color:#D4AF37;">${betTitle}</span> 
                                <span style="font-size:0.85rem; color:#ccc;">(${selectionsText})</span>
                            </div>
                            <div style="text-align:right;">
                                <div class="item-amount">${currentSymbol}${cost.toFixed(2)}</div>
                                <div style="font-size:0.85rem; color:#a7f3d0;">${combinations} comb.</div>
                            </div>
                        </div>
                    `;
                } else {
                    const t = b.win + b.place + b.show;
                    let desglose = [];
                    if (b.win > 0) desglose.push(`<span style="color:#4ade80;">W:${b.win}</span>`);
                    if (b.place > 0) desglose.push(`<span style="color:#60a5fa;">P:${b.place}</span>`);
                    if (b.show > 0) desglose.push(`<span style="color:#cbd5e1;">S:${b.show}</span>`);
                    
                    ticketHtml += `
                        <div class="ticket-item">
                            <div class="item-info">
                                <span style="font-size:1.4rem; font-weight:900; color:#D4AF37;">#${b.horseNumber}</span> 
                                <span style="font-size:0.85rem; color:#ccc;">${b.horseName}</span>
                            </div>
                            <div style="text-align:right;">
                                <div class="item-amount">${currentSymbol}${t.toFixed(2)}</div>
                                <div style="font-size:0.85rem;">(${desglose.join(' ')})</div>
                            </div>
                        </div>
                    `;
                }
            });
        }

        col3.innerHTML = `
            <div class="panel-title" style="background:#000; color:#D4AF37;">4. RESUMEN DE VENTA</div>
            ${currencySelectHtml}
            <div class="ticket-container ticket-items-list" id="ticket-scroll-area">${ticketHtml}</div>

            <div class="mobile-action-bar">
                <div class="total-box">
                    <div class="total-info-group" style="cursor: pointer; display: flex; align-items: center; gap: 10px; user-select: none;">
                        <div class="mobile-only-btn" style="background: rgba(212, 175, 55, 0.15); border: 1px solid #D4AF37; width: 34px; height: 34px; border-radius: 8px; align-items: center; justify-content: center;">
                            <i class="fas fa-chevron-up" id="mobile-toggle-icon" style="color:#D4AF37; font-size:1.1rem; transition: transform 0.25s;"></i>
                        </div>
                        <div>
                            <div class="total-label">TOTAL A COBRAR <span class="mobile-only-btn" style="display:inline !important; font-size:0.65rem; color:#D4AF37;">(Toca p/ opciones)</span></div>
                            <div class="total-amount">${currencySym}${grandTotal.toFixed(2)}</div>
                        </div>
                    </div>
                    
                    <button id="pos-btn-pay-mobile" class="btn-cobrar mobile-only-btn" ${grandTotal > 0 ? '' : 'disabled'}>COBRAR 💵</button>
                </div>
                
                <div class="pay-actions collapsed" id="mobile-pay-actions" style="display:flex; flex-direction:column; gap:10px;">
                    <div style="display:flex; gap:10px; width:100%;">
                        <button id="pos-btn-reprint" title="Reimprimir Último Boleto" style="background:#0284c7; color:white; border:1px solid #38bdf8; border-radius:8px; font-weight:bold; font-size:1.1rem; cursor:pointer; flex: 1; padding:10px; text-transform:uppercase;">🖨️ REIMP.</button>
                        <button id="pos-btn-clear" style="background:#7f1d1d; color:white; border:1px solid #ef4444; border-radius:8px; font-weight:bold; font-size:1.1rem; cursor:pointer; flex: 1; padding:10px; text-transform:uppercase;">🗑️ BORRAR</button>
                    </div>
                    <button id="pos-btn-pay" class="btn-cobrar desktop-only-btn" ${grandTotal > 0 ? '' : 'disabled'} style="width:100%;">COBRAR 💵 (+)</button>
                </div>
            </div>
        `;
        dashboard.appendChild(col3);

        // Control de la gaveta desplegable en móvil
        const toggleGroup = col3.querySelector('.total-info-group');
        const actionsDiv = col3.querySelector('#mobile-pay-actions');
        const toggleIcon = col3.querySelector('#mobile-toggle-icon');

        if (toggleGroup && actionsDiv) {
            if (!window.matchMedia('(max-width: 1024px)').matches) {
                actionsDiv.classList.remove('collapsed');
                toggleGroup.style.cursor = 'default';
            }
            toggleGroup.addEventListener('click', () => {
                if (!window.matchMedia('(max-width: 1024px)').matches) return;
                actionsDiv.classList.toggle('collapsed');
                if (toggleIcon) toggleIcon.classList.toggle('open');
            });
        }

        // ==========================================
        // EVENTOS Y LÓGICA DE VENTA
        // ==========================================
        const updateTimers = () => {
            document.querySelectorAll('.track-timer').forEach(el => {
                const mtp = calculateTimeRemaining(moment.utc(el.dataset.time));
                el.textContent = `| MTP ${mtp}`; el.style.color = mtp === 'MTP 0' ? '#ef4444' : '#facc15';
            });
            const mainTimer = document.getElementById('center-huge-timer');
            if (mainTimer) { mainTimer.textContent = calculateTimeRemaining(moment.utc(mainTimer.dataset.time)); mainTimer.style.color = mainTimer.textContent === 'MTP 0' ? '#ef4444' : '#facc15'; }
        };
        updateTimers();
        uiTimers.push(setInterval(updateTimers, 1000));

        col3.querySelector('#pos-currency-select')?.addEventListener('change', (e) => { 
            if (state.userCurrency && state.userCurrency !== 'BOTH') return; 
            localStorage.setItem('cashier_currency', e.target.value); 
            cashierContext.update({ currency: e.target.value }); 
        });
        
        col1.querySelectorAll('.track-btn').forEach(btn => btn.addEventListener('click', () => {
            document.querySelector('.pos-dashboard')?.setAttribute('data-mobile-step', 'races');
            const track = state.allTracks.find(t => String(t.id) === btn.dataset.id);
            sessionStorage.setItem('last_viewed_track_id', track.id);
            const tRaces = state.allRaces.filter(r => String(r.track_id) === String(track.id));
            
            let targetRace = tRaces.filter(r => r.status === 'scheduled' || r.status === 'open').sort((a,b) => moment.utc(a.start_time).diff(moment.utc(b.start_time)))[0];
            if (!targetRace) targetRace = tRaces.filter(r => r.status === 'finished').sort((a,b) => b.race_number - a.race_number)[0];
            if (!targetRace) targetRace = tRaces.filter(r => r.status === 'closed').sort((a,b) => b.race_number - a.race_number)[0];

            appState.keyboardMode = 'MATRIX'; cashierContext.update({ selectedTrack: track, selectedRace: targetRace || null, bets: [], mobileStep: 'races' });
        }));

        col1.querySelectorAll('.race-btn').forEach(btn => btn.addEventListener('click', () => {
            if (btn.classList.contains('disabled')) return; 
            document.querySelector('.pos-dashboard')?.setAttribute('data-mobile-step', 'matrix');
            appState.bettingMode = 'DIRECT';
            appState.keyboardMode = 'MATRIX'; cashierContext.update({ selectedRace: state.allRaces.find(r => String(r.id) === btn.dataset.id), bets: [], mobileStep: 'matrix' });
        }));

        col1.querySelector('#back-to-tracks')?.addEventListener('click', () => cashierContext.update({ mobileStep: 'tracks', selectedTrack: null, selectedRace: null }));
        col2.querySelector('#back-to-races')?.addEventListener('click', () => cashierContext.update({ selectedRace: null, mobileStep: 'races' }));

        col3.querySelector('#pos-btn-clear').addEventListener('click', () => {
            cashierContext.update({ bets: [] });
        });

        // ⭐ NUEVA LÓGICA: REIMPRESIÓN DEL ÚLTIMO TICKET (CON BÚSQUEDA DE NOMBRES EN MEMORIA CACHÉ) ⭐
        col3.querySelector('#pos-btn-reprint')?.addEventListener('click', async (e) => {
            e.preventDefault();
            const btn = e.currentTarget;
            const originalText = btn.innerHTML;
            btn.innerHTML = '⏳...';
            btn.disabled = true;

            try {
                const currentUser = authService.getCurrentUser();
                const response = await api.get(`/tickets/last-by-cashier/${currentUser.id}`);
                const ticketData = response.ticket || response;

                if (!ticketData || !ticketData.id) {
                    Notification(appState.domElements.notificationContainerCashier, 'No hay boletos recientes.', 'info');
                    return;
                }

                // ⭐ Búsqueda de Poka-Yoke: Extraer Hipódromo y Carrera desde la caché en memoria (state.allRaces)
                const ticketRace = state.allRaces.find(r => String(r.id) === String(ticketData.race_id));
                const ticketTrack = ticketRace ? state.allTracks.find(t => String(t.id) === String(ticketRace.track_id)) : null;

                const publicCodeToPrint = ticketData.public_code || ticketData.ticket_code || ticketData.id;
                
                // Construcción híbrida del objeto a imprimir
                const printData = { 
                    ...ticketData, 
                    id: publicCodeToPrint, 
                    ticket_code: publicCodeToPrint,
                    track_name: ticketTrack ? ticketTrack.name : (ticketData.track_name || 'N/A'),
                    race_number: ticketRace ? ticketRace.race_number : (ticketData.race_number || 'N/A'),
                    race_name: ticketRace ? ticketRace.name : (ticketData.race_name || '')
                };

                // Búsqueda profunda de los nombres de los caballos
                if (ticketData.mixed_bets_details) {
                    let mixDetails = ticketData.mixed_bets_details;
                    if (typeof mixDetails === 'string') { 
                        try { mixDetails = JSON.parse(mixDetails); } catch(err) { mixDetails = []; } 
                    }
                    printData.bet_type_name = 'Mixto';
                    printData.selected_horse_names = mixDetails.map(b => {
                        let hNum = b.horseNumber || b.horse_number || b.horse_id;
                        let hName = b.horseName || b.horse_name || b.name || '';
                        
                        // Si el nombre viene vacío de BD, lo buscamos en los runners de la carrera en caché
                        if (ticketRace && ticketRace.runners) {
                            const runner = ticketRace.runners.find(r => String(r.id) === String(b.horse_id));
                            if (runner) {
                                hNum = runner.horse_number;
                                hName = runner.name;
                            }
                        }
                        return `#${hNum} ${hName} (${(b.type || '').toUpperCase()}: $${parseFloat(b.amount).toFixed(2)})`;
                    });
                } else {
                    // Para tickets no mixtos (Exóticas o directas simples)
                    printData.selected_horse_names = ticketData.horse_names ? ticketData.horse_names : (ticketData.horse_numbers ? ticketData.horse_numbers.map(n => `#${n}`) : []);
                }

                await printTicketReceipt(printData);
                Notification(appState.domElements.notificationContainerCashier, 'Reimprimiendo...', 'success');

            } catch (error) {
                console.error(error);
                Notification(appState.domElements.notificationContainerCashier, 'Error al buscar el boleto.', 'error');
            } finally {
                btn.innerHTML = originalText;
                btn.disabled = false;
                document.querySelector('.matrix-input')?.focus();
            }
        });

        col2.querySelector('#mode-direct')?.addEventListener('click', () => {
            if (appState.bettingMode === 'DIRECT') return;
            appState.bettingMode = 'DIRECT';
            renderDashboard(state);
        });
        col2.querySelector('#mode-exotic')?.addEventListener('click', () => {
            if (appState.bettingMode === 'EXOTIC') return;
            appState.bettingMode = 'EXOTIC';
            renderDashboard(state);
        });

        const showSaleConfirmationModal = (executeSaleCallback, totalOverride = null) => {
            const totalToShow = totalOverride !== null ? totalOverride : grandTotal;
            if (totalToShow <= 0) return;
            if (document.querySelector('.sale-confirmation-overlay')) return; 

            const overlay = document.createElement('div');
            overlay.className = 'modal-overlay visible sale-confirmation-overlay';
            overlay.style.zIndex = '999999';

            const isMobileView = window.matchMedia('(max-width: 1024px)').matches;

            const desktopModalHTML = `
                <div style="background: #022c22; border: 4px solid #D4AF37; border-radius: 16px; padding: 40px; text-align: center; width: 90%; max-width: 700px; box-shadow: 0 0 50px rgba(0,0,0,0.8);">
                    <h2 style="color: #D4AF37; font-size: 2.5rem; font-weight: 900; text-transform: uppercase; margin-top:0; border-bottom: 2px dashed #047857; padding-bottom: 15px;">¿CONFIRMAR VENTA?</h2>
                    <div style="font-size: 2.2rem; color: #fff; margin: 20px 0; font-weight: bold;">
                        ${state.selectedTrack.name} <br>
                        <span style="color: #4ade80;">CARRERA ${state.selectedRace.race_number}</span>
                    </div>
                    <div style="font-size: 6rem; font-weight: 900; color: #facc15; text-shadow: 0 4px 10px rgba(0,0,0,0.5); margin: 30px 0; line-height: 1;">
                        ${currencySym}${totalToShow.toFixed(2)}
                    </div>
                    <div style="display: flex; gap: 15px; justify-content: center; align-items: stretch; margin-top: 30px;">
                        <button id="btn-conf-cancel" style="background: #7f1d1d; color: white; border: 2px solid #ef4444; padding: 15px 20px; font-size: 1.2rem; font-weight: bold; border-radius: 12px; cursor: pointer; flex: 0.9;">[ESC] CANCELAR</button>
                        <button id="btn-conf-wa" title="Confirmar Venta y Enviar Boleto por WhatsApp" style="background: #128C7E; color: white; border: 2px solid #25D366; padding: 15px 20px; font-size: 1.3rem; border-radius: 12px; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; font-weight: 900; flex: 1.1;">
                            <i class="fab fa-whatsapp" style="font-size: 1.6rem;"></i> VENDER + WA
                        </button>
                        <button id="btn-conf-ok" style="background: #15803d; color: white; border: 2px solid #4ade80; padding: 15px 25px; font-size: 1.3rem; font-weight: 900; border-radius: 12px; cursor: pointer; flex: 1.3;">[+] o [ENTER] COBRAR</button>
                    </div>
                </div>
            `;

            const mobileModalHTML = `
                <div style="background: #022c22; border: 3px solid #D4AF37; border-radius: 16px; padding: 20px; text-align: center; width: 95%; max-width: 420px; box-shadow: 0 0 50px rgba(0,0,0,0.85);">
                    <h2 style="color: #D4AF37; font-size: 1.45rem; font-weight: 900; text-transform: uppercase; margin-top:0; border-bottom: 2px dashed #047857; padding-bottom: 10px; margin-bottom: 12px;">¿CONFIRMAR VENTA?</h2>
                    <div style="font-size: 1.15rem; color: #fff; margin: 8px 0; font-weight: bold;">
                        ${state.selectedTrack.name} - <span style="color: #4ade80;">CARRERA ${state.selectedRace.race_number}</span>
                    </div>
                    <div style="font-size: 3.6rem; font-weight: 900; color: #facc15; text-shadow: 0 4px 10px rgba(0,0,0,0.5); margin: 16px 0; line-height: 1;">
                        ${currencySym}${totalToShow.toFixed(2)}
                    </div>
                    <div style="display: flex; gap: 8px; justify-content: center; align-items: stretch; margin-top: 20px;">
                        <button id="btn-conf-cancel" style="background: #7f1d1d; color: white; border: 2px solid #ef4444; padding: 12px 10px; font-size: 0.95rem; font-weight: bold; border-radius: 10px; cursor: pointer; flex: 0.8;">CANC.</button>
                        <button id="btn-conf-wa" title="Confirmar Venta y Compartir por WhatsApp" style="background: #128C7E; color: white; border: 2px solid #25D366; padding: 12px 12px; font-size: 1rem; border-radius: 10px; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px; font-weight: 900; flex: 1.1;">
                            <i class="fab fa-whatsapp" style="font-size: 1.35rem;"></i> VENDER
                        </button>
                        <button id="btn-conf-ok" style="background: #15803d; color: white; border: 2px solid #4ade80; padding: 12px 12px; font-size: 1.05rem; font-weight: 900; border-radius: 10px; cursor: pointer; flex: 1.2;">COBRAR 💵</button>
                    </div>
                </div>
            `;

            overlay.innerHTML = isMobileView ? mobileModalHTML : desktopModalHTML;

            document.body.appendChild(overlay);
            if (document.activeElement) document.activeElement.blur();

            const closeIt = () => {
                if (document.body.contains(overlay)) document.body.removeChild(overlay);
                const firstInput = document.querySelector('.matrix-input');
                if (firstInput) firstInput.focus();
            };

            overlay.querySelector('#btn-conf-cancel').addEventListener('click', closeIt);
            
            // Opción 1: Confirmar Venta Estándar (Imprimir, cerrar modal y volver a la carrera)
            overlay.querySelector('#btn-conf-ok').addEventListener('click', () => { 
                closeIt(); 
                executeSaleCallback(false, null); 
            });
            
            // Opción 2: Confirmar Venta + WhatsApp (Sella la venta, abre WhatsApp, cierra modal y vuelve a la carrera)
            overlay.querySelector('#btn-conf-wa').addEventListener('click', () => { 
                // Abrimos la ventana en el instante del clic para evitar bloqueo de popups en iOS/Android
                const waPopup = window.open('about:blank', '_blank');
                closeIt(); 
                executeSaleCallback(true, waPopup); 
            });
        };

        const executeFinalSale = async (betsOverride = null, shareViaWa = false, waPopup = null) => {
            if (isProcessingSale) {
                if (waPopup) waPopup.close();
                return;
            }
            isProcessingSale = true;
            const btnPay = col3.querySelector('#pos-btn-pay');
            const btnPayMob = col3.querySelector('#pos-btn-pay-mobile');
            if(btnPay) { btnPay.disabled = true; btnPay.innerHTML = 'PROCESANDO...'; }
            if(btnPayMob) { btnPayMob.disabled = true; btnPayMob.innerHTML = '⏳...'; }

            const betsToProcess = betsOverride || state.bets;
            const totalToProcess = betsToProcess.reduce((sum, bet) => {
                if (bet.type === 'EXOTIC') {
                    return sum + (bet.details?.cost || 0);
                }
                return sum + (bet.win || 0) + (bet.place || 0) + (bet.show || 0);
            }, 0);

            if (totalToProcess <= 0) {
                if (waPopup) waPopup.close();
                Notification(appState.domElements.notificationContainerCashier, 'No hay apuestas válidas para procesar.', 'warning');
                isProcessingSale = false;
                if(btnPay) { btnPay.disabled = false; btnPay.innerHTML = 'COBRAR 💵 (+)'; }
                if(btnPayMob) { btnPayMob.disabled = false; btnPayMob.innerHTML = 'COBRAR 💵'; }
                return;
            }

            try {
                const checkRes = await api.get(`/races/${state.selectedRace.id}`);
                const rData = checkRes.race || checkRes;
                if (rData.status !== 'open' && rData.status !== 'scheduled') {
                    if (waPopup) waPopup.close();
                    Notification(appState.domElements.notificationContainerCashier, 'La carrera cerró antes de procesar.', 'error');
                    cashierContext.update({ allRaces: state.allRaces.map(r => r.id === rData.id ? {...r, status: rData.status} : r), selectedRace: {...state.selectedRace, status: rData.status}, bets: [] });
                    return;
                }

                let mixPayload = [];
                betsToProcess.forEach(bet => {
                    if (bet.type === 'EXOTIC') {
                        mixPayload.push({ type: 'Exotic', amount: bet.details.cost, details: bet.details });
                    } else {
                        if (bet.win > 0) mixPayload.push({ race_id: state.selectedRace.id, horse_id: bet.horseId, type: 'Ganador', amount: bet.win });
                        if (bet.place > 0) mixPayload.push({ race_id: state.selectedRace.id, horse_id: bet.horseId, type: 'Place', amount: bet.place });
                        if (bet.show > 0) mixPayload.push({ race_id: state.selectedRace.id, horse_id: bet.horseId, type: 'Show', amount: bet.show });
                    }
                });

                const user = authService.getCurrentUser();
                const response = await api.post('/tickets/sell', { race_id: parseInt(state.selectedRace.id), bet_type_id: null, selected_horses_ids: [], amount: totalToProcess, cashier_id: user.id, currency: state.currency, customer_phone_number: '00', mixed_bets_details: mixPayload, is_exotic: betsToProcess.some(b => b.type === 'EXOTIC') });
                
                if (response.ticket) {
                    Notification(appState.domElements.notificationContainerCashier, 'Venta exitosa.', 'success');
                    document.querySelector('.pos-dashboard')?.setAttribute('data-mobile-step', 'matrix');

                    const formattedHorseLines = mixPayload.map(b => {
                        if (b.type === 'Exotic') {
                            return `${b.details.type} (${currencySym}${parseFloat(b.amount).toFixed(2)})`;
                        }
                        const originalBet = betsToProcess.find(hb => hb.horseId === b.horse_id && (hb.win > 0 || hb.place > 0 || hb.show > 0));
                        const horseNumber = originalBet ? originalBet.horseNumber : '??';
                        return `#${horseNumber} ${originalBet ? originalBet.horseName : ''} (${b.type.toUpperCase()}: ${currencySym}${b.amount})`;
                    });

                    if (shareViaWa) {
                        // ⭐ FIX CRÍTICO PARA COBRO: Usamos el ID interno cobrable del boleto
                        const claimableTicketId = response.ticket.id;
                        const refPublica = response.ticket.public_code || '';
                        const dateNow = moment().tz(VENEZUELA_TIMEZONE).format('DD/MM/YYYY hh:mm A');
                        const waLines = [
                            `🐎 *COMPROBANTE OFICIAL DE APUESTA*`,
                            `━━━━━━━━━━━━━━━━━━━━`,
                            `🏟️ *Hipódromo:* ${state.selectedTrack.name}`,
                            `🏁 *Carrera:* #${state.selectedRace.race_number}`,
                            `📅 *Fecha:* ${dateNow}`,
                            `🔑 *ID DE COBRO:* *${claimableTicketId}*`,
                            ...(refPublica ? [`🎟️ *Ref:* ${refPublica}`] : []),
                            `━━━━━━━━━━━━━━━━━━━━`,
                            `*DETALLE DE JUGADA:*`,
                            ...formattedHorseLines.map(line => `• ${line}`),
                            `━━━━━━━━━━━━━━━━━━━━`,
                            `💰 *TOTAL PAGADO: ${currencySym}${totalToProcess.toFixed(2)} (${state.currency})*`,
                            `━━━━━━━━━━━━━━━━━━━━`,
                            `_Presente su ID DE COBRO (${claimableTicketId}) para hacer efectivo su premio._`
                        ].join('\n');

                        const waUrl = `https://wa.me/?text=${encodeURIComponent(waLines)}`;
                        if (waPopup) {
                            waPopup.location.href = waUrl;
                        } else {
                            window.open(waUrl, '_blank');
                        }
                    
                    } else {
                        await printTicketReceipt({ ...response.ticket, race_name: state.selectedRace.name, race_number: state.selectedRace.race_number, bet_type_name: 'Mixto', track_name: state.selectedTrack.name, amount: totalToProcess, currency: state.currency, 
                            selected_horse_names: formattedHorseLines 
                        });
                    }

                    // Regresa automáticamente a la pantalla de la carrera con los campos limpios
                    sessionStorage.setItem('last_viewed_track_id', state.selectedTrack.id);
                    setTimeout(() => softReload(true), 800);
                }
            } catch (error) { 
                if (waPopup) waPopup.close();
                if (checkAuthError(error)) return; 
                Notification(appState.domElements.notificationContainerCashier, error.message || 'Error al vender', 'error'); 
                if(btnPay) { btnPay.disabled = false; btnPay.innerHTML = 'COBRAR 💵 (+)'; }
                if(btnPayMob) { btnPayMob.disabled = false; btnPayMob.innerHTML = 'COBRAR 💵'; }
            } finally { isProcessingSale = false; }
        };

        if (raceActive) {
            const inputs = Array.from(col2.querySelectorAll('.matrix-input'));
            const maxRow = inputs.length > 0 ? Math.max(...inputs.map(i => parseInt(i.dataset.row))) : 0;

            const processMatrixToTicket = (forceCharge = false) => {
                const isMobileView = window.matchMedia('(max-width: 1024px)').matches;
                let newBets = isMobileView ? [] : [...state.bets];
                let hasValidBets = false; 
                let limitError = null;
                const allLimits = appState.betLimits || {};
                const currentCurrency = (cashierContext.state.currency || 'USD').toLowerCase();
                const limits = allLimits[currentCurrency] || {};

                for (let r = 0; r <= maxRow; r++) {
                    const winInp = inputs.find(i => parseInt(i.dataset.row) === r && parseInt(i.dataset.col) === 0);
                    const placeInp = inputs.find(i => parseInt(i.dataset.row) === r && parseInt(i.dataset.col) === 1);
                    const showInp = inputs.find(i => parseInt(i.dataset.row) === r && parseInt(i.dataset.col) === 2);
                    if (!winInp) continue;
                    const w = parseFloat(winInp.value) || 0; const p = parseFloat(placeInp.value) || 0; const s = parseFloat(showInp.value) || 0;
                    
                    if (w > 0) {
                        if (limits.win_min > 0 && w < limits.win_min) limitError = `Apuesta mínima para WIN es ${limits.win_min}`;
                        if (limits.win_max > 0 && w > limits.win_max) limitError = `Apuesta máxima para WIN es ${limits.win_max}`;
                    }
                    if (p > 0) {
                        if (limits.place_min > 0 && p < limits.place_min) limitError = `Apuesta mínima permitida para PLACE es de ${limits.place_min}`;
                        if (limits.place_max > 0 && p > limits.place_max) limitError = `Apuesta máxima permitida para PLACE es de ${limits.place_max}`;
                    }
                    if (s > 0) {
                        if (limits.show_min > 0 && s < limits.show_min) limitError = `Apuesta mínima permitida para SHOW es de ${limits.show_min}`;
                        if (limits.show_max > 0 && s > limits.show_max) limitError = `Apuesta máxima permitida para SHOW es de ${limits.show_max}`;
                    }
                    if (limitError) break;

                    if (w > 0 || p > 0 || s > 0) { newBets.push({ horseId: parseInt(winInp.dataset.hid), horseNumber: parseInt(winInp.dataset.hnum), horseName: winInp.dataset.hname, win: w, place: p, show: s }); hasValidBets = true; }
                }

                if (limitError) { return Notification(appState.domElements.notificationContainerCashier, limitError, 'error'); }

                // ⭐ FIX CRÍTICO: Calcular total nuevo en tiempo real para no usar grandTotal viejo al presionar [+]
                const newCalculatedTotal = newBets.reduce((sum, b) => {
                    if (b.type === 'EXOTIC') return sum + (b.details?.cost || 0);
                    return sum + (b.win || 0) + (b.place || 0) + (b.show || 0);
                }, 0);

                if (hasValidBets) {
                    cashierContext.update({ bets: newBets });
                    if (forceCharge) setTimeout(() => showSaleConfirmationModal((shareWa, waPopup) => executeFinalSale(null, shareWa, waPopup), newCalculatedTotal), 100);
                    else setTimeout(() => { const t = document.getElementById('ticket-scroll-area'); if(t) t.scrollTop = t.scrollHeight; document.querySelector('.matrix-input')?.focus(); }, 50);
                } else if (forceCharge || state.bets.length > 0) {
                    if (state.bets.length > 0) showSaleConfirmationModal((shareWa, waPopup) => executeFinalSale(null, shareWa, waPopup), grandTotal);
                } else inputs[0]?.focus();
            };

            inputs.forEach(input => {
                input.addEventListener('keydown', (e) => {
                    if (e.key === '+') { e.preventDefault(); e.stopPropagation(); processMatrixToTicket(true); return; }
                    if (appState.keyboardMode !== 'MATRIX') return;
                    const row = parseInt(input.dataset.row); const col = parseInt(input.dataset.col);
                    if (e.key === 'ArrowUp') { e.preventDefault(); inputs.find(i => parseInt(i.dataset.row) === row - 1 && parseInt(i.dataset.col) === col)?.focus(); } 
                    else if (e.key === 'ArrowDown') { e.preventDefault(); inputs.find(i => parseInt(i.dataset.row) === row + 1 && parseInt(i.dataset.col) === col)?.focus(); }
                    else if (e.key === 'ArrowRight') { e.preventDefault(); (inputs.find(i => parseInt(i.dataset.row) === row && parseInt(i.dataset.col) === col + 1) || inputs.find(i => parseInt(i.dataset.row) === row + 1 && parseInt(i.dataset.col) === 0))?.focus(); }
                    else if (e.key === 'ArrowLeft') { e.preventDefault(); (inputs.find(i => parseInt(i.dataset.row) === row && parseInt(i.dataset.col) === col - 1) || inputs.find(i => parseInt(i.dataset.row) === row - 1 && parseInt(i.dataset.col) === 2))?.focus(); }
                    else if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); inputs.some(i => i.value !== '') ? processMatrixToTicket(false) : (state.bets.length > 0 && showSaleConfirmationModal((shareWa, waPopup) => executeFinalSale(null, shareWa, waPopup))); }
                });
                input.addEventListener('focus', function() { appState.keyboardMode = 'MATRIX'; this.select(); col1.querySelector('#track-list-box').style.boxShadow = ''; col1.querySelector('#race-list-box').style.boxShadow = ''; });
            });

            // ⭐ FIX CRÍTICO: Declaración previa de variables para evitar ReferenceError
            const btnPayDesktop = col3.querySelector('#pos-btn-pay');
            const btnPayMobile = col3.querySelector('#pos-btn-pay-mobile');

            const isMobileViewForListener = window.matchMedia('(max-width: 1024px)').matches;
            if (isMobileViewForListener) {
                const totalAmountEl = col3.querySelector('.total-amount');
                const currencySym = state.availableCurrencies?.find(c => c.code === state.currency)?.symbol || '\$';

                const updateMobileTotalDisplay = () => {
                    let currentTotal = 0;
                    inputs.forEach(inp => {
                        currentTotal += parseFloat(inp.value) || 0;
                    });

                    if (totalAmountEl) {
                        totalAmountEl.textContent = `${currencySym}${currentTotal.toFixed(2)}`;
                    }
                    if (btnPayDesktop) btnPayDesktop.disabled = currentTotal <= 0;
                    if (btnPayMobile) btnPayMobile.disabled = currentTotal <= 0;
                };

                inputs.forEach(input => {
                    input.addEventListener('input', updateMobileTotalDisplay);
                });
                updateMobileTotalDisplay();
            }

            const handlePayClick = (e) => { 
                e.preventDefault();
                const isMobileView = window.matchMedia('(max-width: 1024px)').matches;
                if (isMobileView) {
                    const inputs = Array.from(col2.querySelectorAll('.matrix-input'));
                    const maxRow = inputs.length > 0 ? Math.max(...inputs.map(i => parseInt(i.dataset.row))) : 0;
                    const newBets = [];
                    let hasValidBets = false;
                    let mobileTotal = 0;
                    let limitError = null;
                    const allLimits = appState.betLimits || {};
                    const currentCurrency = (cashierContext.state.currency || 'USD').toLowerCase();
                    const limits = allLimits[currentCurrency] || {};

                    for (let r = 0; r <= maxRow; r++) {
                        const winInp = inputs.find(i => parseInt(i.dataset.row) === r && parseInt(i.dataset.col) === 0);
                        if (!winInp) continue;
                        const placeInp = inputs.find(i => parseInt(i.dataset.row) === r && parseInt(i.dataset.col) === 1);
                        const showInp = inputs.find(i => parseInt(i.dataset.row) === r && parseInt(i.dataset.col) === 2);
                        const w = parseFloat(winInp.value) || 0, p = parseFloat(placeInp.value) || 0, s = parseFloat(showInp.value) || 0;
                        
                        if (w > 0) {
                            if (limits.win_min > 0 && w < limits.win_min) limitError = `Apuesta mínima para WIN es ${limits.win_min}`;
                            if (limits.win_max > 0 && w > limits.win_max) limitError = `Apuesta máxima para WIN es ${limits.win_max}`;
                        }
                        if (p > 0) {
                            if (limits.place_min > 0 && p < limits.place_min) limitError = `Apuesta mínima permitida para PLACE es de ${limits.place_min}`;
                            if (limits.place_max > 0 && p > limits.place_max) limitError = `Apuesta máxima permitida para PLACE es de ${limits.place_max}`;
                        }
                        if (s > 0) {
                            if (limits.show_min > 0 && s < limits.show_min) limitError = `Apuesta mínima permitida para SHOW es de ${limits.show_min}`;
                            if (limits.show_max > 0 && s > limits.show_max) limitError = `Apuesta máxima permitida para SHOW es de ${limits.show_max}`;
                        }
                        if (limitError) break;

                        if (w > 0 || p > 0 || s > 0) {
                            newBets.push({ horseId: parseInt(winInp.dataset.hid), horseNumber: parseInt(winInp.dataset.hnum), horseName: winInp.dataset.hname, win: w, place: p, show: s });
                            hasValidBets = true;
                            mobileTotal += w + p + s;
                        }
                    }

                    if (limitError) { return Notification(appState.domElements.notificationContainerCashier, limitError, 'error'); }

                    if (hasValidBets) {
                        // ⭐ FIX CRÍTICO: Se pasa waPopup en la firma para que la ventana puente de WhatsApp sobreviva
                        showSaleConfirmationModal((shareWa, waPopup) => executeFinalSale(newBets, shareWa, waPopup), mobileTotal);
                    }
                } else {
                    showSaleConfirmationModal((shareWa, waPopup) => executeFinalSale(null, shareWa, waPopup));
                }
            };

            if (btnPayDesktop) btnPayDesktop.addEventListener('click', handlePayClick);
            if (btnPayMobile) btnPayMobile.addEventListener('click', handlePayClick);

            col3.querySelector('#pos-btn-pay').addEventListener('click', (e) => { 
                e.preventDefault();
                const isMobileView = window.matchMedia('(max-width: 1024px)').matches;
                if (isMobileView) {
                    const inputs = Array.from(col2.querySelectorAll('.matrix-input'));
                    const maxRow = inputs.length > 0 ? Math.max(...inputs.map(i => parseInt(i.dataset.row))) : 0;
                    const newBets = [];
                    let hasValidBets = false;
                    let mobileTotal = 0;
                    let limitError = null;
                    const allLimits = appState.betLimits || {};
                    const currentCurrency = (cashierContext.state.currency || 'USD').toLowerCase();
                    const limits = allLimits[currentCurrency] || {};

                    for (let r = 0; r <= maxRow; r++) {
                        const winInp = inputs.find(i => parseInt(i.dataset.row) === r && parseInt(i.dataset.col) === 0);
                        if (!winInp) continue;
                        const placeInp = inputs.find(i => parseInt(i.dataset.row) === r && parseInt(i.dataset.col) === 1);
                        const showInp = inputs.find(i => parseInt(i.dataset.row) === r && parseInt(i.dataset.col) === 2);
                        const w = parseFloat(winInp.value) || 0, p = parseFloat(placeInp.value) || 0, s = parseFloat(showInp.value) || 0;
                        
                        if (w > 0) {
                            if (limits.win_min > 0 && w < limits.win_min) limitError = `Apuesta mínima para WIN es ${limits.win_min}`;
                            if (limits.win_max > 0 && w > limits.win_max) limitError = `Apuesta máxima para WIN es ${limits.win_max}`;
                        }
                        if (p > 0) {
                            if (limits.place_min > 0 && p < limits.place_min) limitError = `Apuesta mínima permitida para PLACE es de ${limits.place_min}`;
                            if (limits.place_max > 0 && p > limits.place_max) limitError = `Apuesta máxima permitida para PLACE es de ${limits.place_max}`;
                        }
                        if (s > 0) {
                            if (limits.show_min > 0 && s < limits.show_min) limitError = `Apuesta mínima permitida para SHOW es de ${limits.show_min}`;
                            if (limits.show_max > 0 && s > limits.show_max) limitError = `Apuesta máxima permitida para SHOW es de ${limits.show_max}`;
                        }
                        if (limitError) break;

                        if (w > 0 || p > 0 || s > 0) {
                            newBets.push({ horseId: parseInt(winInp.dataset.hid), horseNumber: parseInt(winInp.dataset.hnum), horseName: winInp.dataset.hname, win: w, place: p, show: s });
                            hasValidBets = true;
                            mobileTotal += w + p + s;
                        }
                    }

                    if (limitError) { return Notification(appState.domElements.notificationContainerCashier, limitError, 'error'); }

                    if (hasValidBets) {
                        showSaleConfirmationModal((shareWa, waPopup) => executeFinalSale(newBets, shareWa, waPopup), mobileTotal);
                    }
                } else {
                    showSaleConfirmationModal((shareWa, waPopup) => executeFinalSale(null, shareWa, waPopup));
                }
            });

            const syncTimer = setInterval(async () => {
                if (isProcessingSale) return;
                try {
                    const res = await api.get(`/races/${state.selectedRace.id}`);
                    const fr = res.race || res;
                    if (fr && (fr.status === 'closed' || fr.status === 'finished') && state.selectedRace.status !== fr.status) {
                        const closedRaceTrackId = state.selectedTrack.id;
                        const updatedRaces = state.allRaces.map(r => r.id === fr.id ? { ...r, status: fr.status } : r);
                        
                        cashierContext.update({
                            allRaces: updatedRaces,
                            selectedRace: { ...state.selectedRace, status: fr.status },
                            bets: []
                        });
                        Notification(appState.domElements.notificationContainerCashier, '¡Carrera Cerrada!', 'warning');

                        setTimeout(() => {
                            const currentTrackIdInView = sessionStorage.getItem('last_viewed_track_id');
                            if (String(currentTrackIdInView) === String(closedRaceTrackId)) {
                                const tRaces = updatedRaces.filter(r => String(r.track_id) === String(closedRaceTrackId));
                                const nRace = tRaces.filter(r => r.status === 'scheduled' || r.status === 'open').sort((a, b) => moment.utc(a.start_time).diff(moment.utc(b.start_time)))[0];
                                if (nRace) {
                                    cashierContext.update({ selectedRace: nRace, bets: [] });
                                    Notification(appState.domElements.notificationContainerCashier, 'Avanzando a la siguiente carrera...', 'info');
                                }
                            }
                        }, 60000);
                    }
                } catch(e){
                if (checkAuthError(e)) { clearInterval(syncTimer); }
                }
            }, 2000);
            uiTimers.push(syncTimer);

            setTimeout(() => { if (inputs.length > 0 && appState.keyboardMode === 'MATRIX') inputs[0].focus(); }, 100);
        }

        // ==========================================
        // KEYBOARD GLOBAL
        // ==========================================
        if (appState.globalKeyHandler) document.removeEventListener('keydown', appState.globalKeyHandler);
        appState.globalKeyHandler = function(e) {
            if (document.activeElement && (document.activeElement.type === 'text' || document.activeElement.type === 'password' || document.activeElement.type === 'tel' || document.activeElement.type === 'date')) return;
            const confModal = document.querySelector('.sale-confirmation-overlay');
            if (confModal) {
                if (e.key === '+' || e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); document.getElementById('btn-conf-ok')?.click(); } 
                else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); document.getElementById('btn-conf-cancel')?.click(); }
                return; 
            }

            if (appState.bettingMode === 'EXOTIC') {
                e.preventDefault();
                switch (e.key) {
                    case 'ArrowUp': window.wizardNavigate('Up'); break;
                    case 'ArrowDown': window.wizardNavigate('Down'); break;
                    case 'ArrowLeft': window.wizardNavigate('Left'); break;
                    case 'ArrowRight': window.wizardNavigate('Right'); break;
                    case ' ': window.wizardSelect(); break; 
                    case 'Enter': 
                    case '+':
                        window.wizardProcess();
                        break;
                    case 'F5': window.wizardSwitchBet('EXACTA'); break;
                    case 'F6': window.wizardSwitchBet('TRIFECTA'); break;
                    case 'F7': window.wizardSwitchBet('SUPERFECTA'); break;
                }
                return;
            }

            if (e.key === '+' || (e.key === 'Enter' && (!document.activeElement || !document.activeElement.classList.contains('matrix-input')))) {
                e.preventDefault(); const btn = document.getElementById('pos-btn-pay'); if (btn && !btn.disabled && !isProcessingSale && state.bets.length > 0) btn.click(); return;
            }
            if (e.key === 'Delete') { e.preventDefault(); cashierContext.update({ bets: [] }); Notification(appState.domElements.notificationContainerCashier, 'Ticket borrado', 'info'); return; }
            if (e.key === '/') { e.preventDefault(); softReload(); return; }
            if (e.key === 'Escape') {
                e.preventDefault();
                const modales = document.querySelectorAll('.modal-overlay.visible, .payout-modal-overlay.visible, .config-popup-overlay.visible');
                if (modales.length > 0) { modales.forEach(m => m.classList.remove('visible')); return; }
                appState.keyboardMode = 'MATRIX'; document.querySelector('.matrix-input')?.focus(); return;
            }
            if (e.key === '*') {
                e.preventDefault(); appState.keyboardMode = 'RACES';
                col1.querySelector('#race-list-box').style.boxShadow = 'inset 0 0 0 3px #facc15'; col1.querySelector('#track-list-box').style.boxShadow = '';
                document.activeElement?.blur(); return;
            }
            if (e.key.toLowerCase() === 'i') {
                e.preventDefault(); appState.keyboardMode = 'TRACKS';
                col1.querySelector('#track-list-box').style.boxShadow = 'inset 0 0 0 3px #facc15'; col1.querySelector('#race-list-box').style.boxShadow = '';
                document.activeElement?.blur(); return;
            }
            if (/^[a-zA-Z]$/.test(e.key)) {
                const letter = e.key.toLowerCase(); e.preventDefault();
                const matched = appState.tracksByLetter[letter];
                if (matched && matched.length > 0) {
                    let nIdx = 0; const cIdx = matched.findIndex(t => state.selectedTrack && String(state.selectedTrack.id) === String(t.id));
                    if (cIdx !== -1) nIdx = (cIdx + 1) % matched.length;
                    col1.querySelector(`.track-btn[data-id="${matched[nIdx].id}"]`)?.click(); 
                }
                return;
            }
            if (e.key >= '1' && e.key <= '9') {
                if (appState.keyboardMode === 'TRACKS') col1.querySelector(`.track-btn[data-idx="${e.key}"]`)?.click();
                else if (appState.keyboardMode === 'RACES') col1.querySelector(`.race-btn[data-idx="${e.key}"]:not(.disabled)`)?.click();
            }
        };
        document.addEventListener('keydown', appState.globalKeyHandler);
    };

    cashierContext.subscribe(renderDashboard);

    async function initializePage(isAfterSale = false) {
        const user = authService.getCurrentUser();

        // --- LÓGICA DE CONFIGURACIÓN POR USUARIO ---
        let userSpecificRate = null;
        if (user) {
            // 1. Moneda Fija: Si el usuario tiene una moneda asignada, la usamos y bloqueamos.
            if (user.currency_code && user.currency_code !== 'BOTH') {
                localStorage.setItem('cashier_currency', user.currency_code);
            }
            // 2. Tasa de Cambio Personalizada: Si no es automática y tiene un valor, la usamos.
            if (user.auto_update_rate === false && user.exchange_rate) {
                userSpecificRate = parseFloat(user.exchange_rate);
            }
        }

        const availableCurrencies = [
            { code: 'USD', symbol: '$' },
            { code: 'VES', symbol: 'Bs.' },
            { code: 'COP', symbol: 'COL$' }
        ];

        // 3. Obtener tasa de cambio (Global o Personalizada)
        try { 
            const conf = await api.get('/configs/cashier'); 
            appState.exchangeRate = userSpecificRate !== null ? userSpecificRate : (parseFloat(conf.exchange_rate) || 0);
            
            // Cargar Límites Avanzados de Win/Place/Show
            let parsedRinconada = typeof conf.rinconada_rules === 'string' ? JSON.parse(conf.rinconada_rules || '{}') : (conf.rinconada_rules || {});
            appState.betLimits = parsedRinconada.bet_limits || {};
        } catch (e) {
            if (checkAuthError(e)) return;
            appState.exchangeRate = userSpecificRate !== null ? userSpecificRate : 0;
        }
        let tks = [], rcs = [], bty = [];
        try { 
            const tr = await api.get('/tracks?admin_mode=true'); 
            const map = new Map(); (tr.tracks || tr.data || tr || []).forEach(t => map.set(t.name.trim().toLowerCase(), t)); tks = Array.from(map.values());
        } catch (e) {
            if (checkAuthError(e)) return;
        }
        
        try { 
            const fetchStart = moment().tz(VENEZUELA_TIMEZONE).subtract(1, 'days').startOf('day').utc().toISOString();
            const fetchEnd = moment().tz(VENEZUELA_TIMEZONE).add(1, 'days').endOf('day').utc().toISOString();
            
            const [u, c, f] = await Promise.all([
                api.get('/races/upcoming').catch(()=>({races:[]})), 
                api.get(`/races?status=closed&start_time_after=${fetchStart}&end_time_before=${fetchEnd}`).catch(()=>({races:[]})), 
                api.get(`/races/results?status=finished&start_time_after=${fetchStart}&end_time_before=${fetchEnd}`).catch(()=>({races:[]}))
            ]);
            
            const map = new Map();
            [u, c, f].forEach(res => {
                const arr = res.races || res.data || res || [];
                if (Array.isArray(arr)) {
                    arr.forEach(r => {
                        if (map.has(r.id)) {
                            const prev = map.get(r.id);
                            map.set(r.id, { ...prev, ...r, runners: r.runners?.length ? r.runners : prev.runners, results: r.results?.length ? r.results : prev.results });
                        } else { map.set(r.id, r); }
                    });
                }
            });
            rcs = Array.from(map.values());
        } catch (e) {
            if (checkAuthError(e)) return;
        }
        
        try { const bt = await api.get('/bet_types'); bty = bt.betTypes || bt || []; } catch (e) {
            if (checkAuthError(e)) return;
        }

        try {
            // --- FILTRADO DE HIPÓDROMOS POR PERMISO DE USUARIO ---
            if (user && Array.isArray(user.allowed_track_ids) && user.allowed_track_ids.length > 0) {
                const allowedIds = new Set(user.allowed_track_ids);
                tks = tks.filter(t => allowedIds.has(t.id));
            }

            const hoy = moment().tz(VENEZUELA_TIMEZONE).format('YYYY-MM-DD');
            const validRaces = rcs.filter(r => {
                if (!r.start_time) return false;
                // Adicionalmente, filtramos carreras para que solo muestre las de los hipódromos permitidos
                const trackIsAllowed = tks.some(tk => tk.id === r.track_id);
                return moment.utc(r.start_time).tz(VENEZUELA_TIMEZONE).format('YYYY-MM-DD') === hoy && trackIsAllowed;
            });
            
            const userCurrency = user ? user.currency_code : null;
            let curr = localStorage.getItem('cashier_currency') || 'USD';
            if (userCurrency === 'BOTH' && curr !== 'USD' && curr !== 'VES') {
                curr = 'USD';
            } else if (userCurrency && userCurrency !== 'BOTH') {
                curr = userCurrency;
            }
            let sTrk = null, sRce = null; 
            const sid = sessionStorage.getItem('last_viewed_track_id');
            
            if (sid) { 
                sTrk = tks.find(t => String(t.id) === sid); 
                if (sTrk) { 
                    const tr = validRaces.filter(r => String(r.track_id) === String(sTrk.id)); 
                    sRce = tr.filter(r => r.status === 'open' || r.status === 'scheduled').sort((a,b) => moment.utc(a.start_time).diff(moment.utc(b.start_time)))[0];
                    if (!sRce) sRce = tr.filter(r => r.status === 'finished').sort((a,b) => b.race_number - a.race_number)[0];
                    if (!sRce) sRce = tr.filter(r => r.status === 'closed').sort((a,b) => b.race_number - a.race_number)[0];
                } 
            }
            
            if (!sTrk && validRaces.length > 0) { 
                const ar = validRaces.filter(r => r.status === 'open' || r.status === 'scheduled').sort((a,b) => moment.utc(a.start_time).diff(moment.utc(b.start_time))); 
                if (ar.length > 0) { 
                    sRce = ar[0]; 
                    sTrk = tks.find(t => String(t.id) === String(sRce.track_id)); 
                } else { 
                    const fr = validRaces.filter(r => r.status === 'finished').sort((a,b) => moment.utc(b.start_time).diff(moment.utc(a.start_time))); 
                    if (fr.length > 0) { 
                        sRce = fr[0]; 
                        sTrk = tks.find(t => String(t.id) === String(sRce.track_id)); 
                    } else { 
                        sTrk = tks[0]; 
                        sRce = validRaces.find(r => String(r.track_id) === String(sTrk.id)); 
                    } 
                } 
            }
            
            if (sTrk) sessionStorage.setItem('last_viewed_track_id', sTrk.id);
            
            // ⭐ FIX MÓVIL: Si es carga inicial (false) va a Hipódromos. Si acaba de vender (true) se queda en Matrix.
            const initialMobileStep = isAfterSale ? 'matrix' : 'tracks';
            cashierContext.update({ 
                availableCurrencies: availableCurrencies,
                exchangeRate: appState.exchangeRate, 
                allTracks: tks, // Pasa los hipódromos ya filtrados
                allRaces: validRaces, // Pasa las carreras ya filtradas
                allBetTypes: bty, 
                currency: curr, 
                userCurrency: userCurrency, // Pasa la moneda fija del usuario al estado
                selectedTrack: sTrk, selectedRace: sRce, mobileStep: initialMobileStep 
            });
        } catch (e) { Notification(appState.domElements.notificationContainerCashier, 'Error', 'error'); }
    }

    initializePage();
    return page;
}